/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useMemo, useEffect } from "react";

import withShipment from "../../withShipment";
import {
  partnersSelector,
  housesSelector,
  currentPersonIdSelector,
  roleSelector,
  canSeeAllSelector,
  addableUsersSelector,
} from "../../redux/selectors/realEstate";
import {
  listRealEstate,
  addPartnerUser as addPartnerUserAction,
  deletePartner as deletePartnerAction,
  saveHouse as saveHouseAction,
  deleteHouse as deleteHouseAction,
  saveExpense as saveExpenseAction,
  deleteExpense as deleteExpenseAction,
} from "../../redux/actions/realEstate";
import CustomToolbar from "../global/CustomToolbar";

/* ============================================================
   Change 12 · Language switcher — English / Español (full UI i18n)
   - `ES` is a source-keyed dictionary: ES["English"] = "Español".
   - t("English") returns the Spanish string when lang === 'es', else the
     English source. Missing keys fall back to English automatically, so
     nothing ever breaks if a string isn't translated yet.
   - Logic, data and numbers never change — only displayed text.
   - User-created categories / partner names / free-text (notes, source,
     descriptions) are NOT translated; t() falls back to the original.
   - The exported PDF / printable report intentionally stays English.
   ============================================================ */
const LANGS = [
  { code: "en", flag: "🇺🇸", label: "English" },
  { code: "es", flag: "🇪🇸", label: "Español" },
];

const ES = {
  // — session bar / switcher —
  "Logged in as": "Conectado como",
  "Switch user": "Cambiar usuario",
  "Preview the module from another partner's seat": "Previsualiza el módulo desde la cuenta de otro socio",
  "Partners": "Socios",
  "PREVIEW": "VISTA PREVIA",
  "YOU": "TÚ",
  "Admin": "Administrador",
  "Partner": "Socio",
  // — list header / tabs / stat strip —
  "Real Estate": "Bienes raíces",
  "Houses Project": "Proyecto de casas",
  "Portfolio": "Portafolio",
  "Active houses": "Casas activas",
  "Closed": "Cerradas",
  "Total invested": "Total invertido",
  "Active": "Activas",
  "Completed": "Completada",
  "Closing": "Cerrando",
  "Rental": "Alquiler",
  "No closed accounts yet.": "Aún no hay cuentas cerradas.",
  "No active houses. Tap + New House to start.": "No hay casas activas. Toca + Nueva casa para empezar.",
  "Est. profit": "Ganancia est.",
  "Est. equity": "Plusvalía est.",
  "Net profit": "Ganancia neta",
  "expenses": "gastos",
  // — toolbar titles —
  "House": "Casa",
  "New House": "Nueva casa",
  "Edit House": "Editar casa",
  "Portfolio Summary": "Resumen del portafolio",
  "Back": "Atrás",
  "Edit": "Editar",
  "PDF": "PDF",
  // — portfolio —
  "property": "propiedad",
  "properties": "propiedades",
  "Active Houses": "Casas activas",
  "Closed Houses": "Casas cerradas",
  "Total Invested": "Total invertido",
  "Est. Profit": "Ganancia est.",
  "Capital Summary": "Resumen de capital",
  "Active Capital": "Capital activo",
  "Closed Capital": "Capital cerrado",
  "Capital at Risk": "Capital en riesgo",
  "Capital at Risk = money in active deals, not yet sold.": "Capital en riesgo = dinero en negocios activos, aún no vendidos.",
  "Profit by Partner — all houses": "Ganancia por socio — todas las casas",
  "No estimated profit yet. Add ARV or a sale price to a house.": "Aún no hay ganancia estimada. Agrega un ARV o un precio de venta a una casa.",
  "Total estimated profit": "Ganancia estimada total",
  "Properties": "Propiedades",
  "No properties yet.": "Aún no hay propiedades.",
  "Est. Equity": "Plusvalía est.",
  "Profit by Partner": "Ganancia por socio",
  "Partner Summary": "Resumen por socio",
  "No partner activity yet.": "Aún no hay actividad de socios.",
  "Total Contributed": "Total aportado",
  "To Receive (settle-up)": "Por recibir (liquidación)",
  "Owes (settle-up)": "Debe (liquidación)",
  "Est. Profit Share": "Ganancia est. (su parte)",
  // — house detail · deal card —
  "Deal": "Negocio",
  "ARV (target value)": "ARV (valor objetivo)",
  "— add in Edit": "— agrégalo en Editar",
  "Purchase price (full)": "Precio de compra (total)",
  "Purchase (from expenses)": "Compra (de los gastos)",
  "Renovation (from expenses)": "Renovación (de los gastos)",
  "= All-in (project cost)": "= Total invertido (costo del proyecto)",
  "Cash invested (your expenses)": "Efectivo invertido (tus gastos)",
  "Days held": "Días en posesión",
  "Holding so far (est.)": "Costo de mantenimiento (est.)",
  "Equity created": "Plusvalía creada",
  "of ARV": "del ARV",
  "Cash flow (est.)": "Flujo de caja (est.)",
  "Projected profit": "Ganancia proyectada",
  "margin": "margen",
  "Renovation budget": "Presupuesto de renovación",
  "Sale result": "Resultado de la venta",
  "Sale price": "Precio de venta",
  "All-in (project cost)": "Total invertido (costo del proyecto)",
  "Selling costs": "Costos de venta",
  "Holding costs": "Costos de mantenimiento",
  "ROI": "ROI",
  // — who paid what —
  "Who paid what": "Quién pagó qué",
  "+ Add partner": "+ Agregar socio",
  "By category": "Por categoría",
  "No expenses logged yet.": "Aún no se han registrado gastos.",
  "Expenses by partner": "Gastos por socio",
  "No expenses yet. Start by logging the purchase price as your first expense.": "Aún no hay gastos. Empieza registrando el precio de compra como tu primer gasto.",
  "Total paid": "Total pagado",
  "View": "Ver",
  // — close out —
  "Close out & settle": "Cerrar y liquidar",
  "Who owes who (preview)": "Quién le debe a quién (vista previa)",
  "✓ Even — no one owes anything.": "✓ A mano — nadie debe nada.",
  "pays": "paga",
  "paid": "pagó",
  "Close account & start settling": "Cerrar cuenta y empezar a liquidar",
  "✓ Even — no payment needed.": "✓ A mano — no se necesita pago.",
  "Paid": "Pagado",
  "✓ Settled": "✓ Liquidado",
  "left": "restante",
  "Remove": "Quitar",
  "+ Record payment": "+ Registrar pago",
  "Amount paid": "Monto pagado",
  "Date": "Fecha",
  "Notes (where / how paid)": "Notas (dónde / cómo se pagó)",
  "Cancel": "Cancelar",
  "Record payment": "Registrar pago",
  "Finalize — close account": "Finalizar — cerrar cuenta",
  "Record the remaining balances to finalize.": "Registra los saldos restantes para finalizar.",
  "Reopen to edit expenses": "Reabrir para editar gastos",
  "Reopen account": "Reabrir cuenta",
  "Report": "Informe",
  "Open / Print": "Abrir / Imprimir",
  "Download PDF": "Descargar PDF",
  "Preparing PDF…": "Preparando PDF…",
  "PDF unavailable": "PDF no disponible",
  // — house form —
  "House name": "Nombre de la casa",
  "Location": "Ubicación",
  "Acquired": "Adquirida",
  "Status": "Estado",
  "Partners in this house": "Socios en esta casa",
  "Ownership split": "División de propiedad",
  "Select at least one partner above.": "Selecciona al menos un socio arriba.",
  "Even split": "División equitativa",
  "Deal type": "Tipo de negocio",
  "🔁 Flip (sell)": "🔁 Revender (vender)",
  "🔑 Keep & rent": "🔑 Conservar y alquilar",
  "Deal economics": "Economía del negocio",
  "ARV (after-repair value)": "ARV (valor después de reparar)",
  "Monthly holding cost": "Costo de mantenimiento mensual",
  "Monthly rent (expected)": "Renta mensual (esperada)",
  "Sale (when sold)": "Venta (cuando se venda)",
  "Notes": "Notas",
  "Save changes": "Guardar cambios",
  "Create house": "Crear casa",
  "Delete house": "Eliminar casa",
  "Tap again to delete house": "Toca de nuevo para eliminar la casa",
  // — expense form —
  "Edit Expense": "Editar gasto",
  "Add Expense": "Agregar gasto",
  "+ Add Expense": "+ Agregar gasto",
  "Paid by": "Pagado por",
  "original payer": "pagador original",
  "your session": "tu sesión",
  "Amount": "Monto",
  "Category": "Categoría",
  "+ New": "+ Nueva",
  "Category name": "Nombre de la categoría",
  "Add category": "Agregar categoría",
  "Description": "Descripción",
  "Source (note)": "Fuente (nota)",
  "Receipts — photos or files": "Recibos — fotos o archivos",
  "Add another": "Agregar otro",
  "Add photo or file": "Agregar foto o archivo",
  "Download": "Descargar",
  "Add expense": "Agregar gasto",
  "Delete expense": "Eliminar gasto",
  "Tap again to delete expense": "Toca de nuevo para eliminar el gasto",
  // — receipts / members / partners modals —
  "Receipts": "Recibos",
  "No receipts attached.": "No hay recibos adjuntos.",
  "Open": "Abrir",
  "Done": "Listo",
  "In this house": "En esta casa",
  "Add partner": "Agregar socio",
  "Select a user…": "Selecciona un usuario…",
  "Add": "Agregar",
  "Current partners": "Socios actuales",
  "admin": "administrador",
  "has expenses": "tiene gastos",
  "Revoke access?": "¿Revocar acceso?",
  // — toasts —
  "Changes saved ✓": "Cambios guardados ✓",
  "House created ✓": "Casa creada ✓",
  "House deleted": "Casa eliminada",
  "Expense added ✓": "Gasto agregado ✓",
  "Expense deleted": "Gasto eliminado",
  "Access revoked ✓": "Acceso revocado ✓",
  "Partner added ✓": "Socio agregado ✓",
  "Account closing — confirm payments": "Cerrando cuenta — confirma los pagos",
  "Payment recorded ✓": "Pago registrado ✓",
  "Account closed ✓": "Cuenta cerrada ✓",
  "Account reopened": "Cuenta reabierta",
  // — additional fragments —
  "Across": "En",
  "Total": "Total",
  "over": "de más",
  "of": "de",
  "owed": "que le toca",
  "days": "días",
  "Account closed": "Cuenta cerrada",
  "verifying payments": "verificando pagos",
  "Reno spent": "Renovación gastada",
  "Over budget by": "Sobre el presupuesto por",
  "Books frozen on": "Cuentas congeladas el",
  "Record payments (partial allowed), then finalize.": "Registra los pagos (se permiten parciales), luego finaliza.",
  "Select date": "Selecciona fecha",
  "Date is in the future — double-check it.": "La fecha está en el futuro — verifícala.",
  "Amount must be greater than 0.": "El monto debe ser mayor que 0.",
  "What was it for?": "¿Para qué fue?",
  "about": "aprox.",
  "e.g. 295000 — full property price": "ej. 295000 — precio total de la propiedad",
  "interest + taxes + insurance + utilities + HOA": "interés + impuestos + seguro + servicios + HOA",
  "commission + closing": "comisión + cierre",
  "Equity created = ARV − all-in (full purchase price + reno). Cash flow ≈ rent − monthly holding. Down payments are still logged as 'purchase' expenses.":
    "Plusvalía creada = ARV − total invertido (precio total + renovación). Flujo de caja ≈ renta − mantenimiento mensual. Los enganches se siguen registrando como gastos de 'compra'.",
  "Profit = ARV − all-in (full purchase price + reno) − holding. Down payments are still logged as 'purchase' expenses. Leave purchase price blank to fall back to those expenses.":
    "Ganancia = ARV − total invertido (precio total + renovación) − mantenimiento. Los enganches se siguen registrando como gastos de 'compra'. Deja el precio de compra en blanco para usar esos gastos.",
  "Selling costs (agent + closing) usually run ≈ 8% of sale price": "Los costos de venta (agente + cierre) suelen ser ≈ 8% del precio de venta",
  "To add a new partner, create their account in": "Para agregar un socio nuevo, crea su cuenta en",
  "and grant": "y otorga acceso a",
  "access — they'll then appear in this list.": "— luego aparecerá en esta lista.",
  "Everyone with Real Estate access is already on this house. To add someone new, create their account in": "Todos los que tienen acceso a Bienes raíces ya están en esta casa. Para agregar a alguien nuevo, crea su cuenta en",
  "access.": "acceso.",
  "Adding a partner resets the split to an even share — adjust it in Edit.": "Agregar un socio restablece la división a partes iguales — ajústala en Editar.",
  "Partners are the company users with": "Los socios son los usuarios de la empresa con acceso a",
  "access. Pick a user below to grant access instantly. Removing a partner revokes their Real Estate access.": "Elige un usuario abajo para otorgar acceso al instante. Quitar a un socio revoca su acceso a Bienes raíces.",
  "All company users already have Real Estate access. Create new accounts in Manage Employee.": "Todos los usuarios de la empresa ya tienen acceso a Bienes raíces. Crea cuentas nuevas en Administrar empleados.",
  // — seeded category names —
  "Purchase": "Compra",
  "Paint": "Pintura",
  "Repairs": "Reparaciones",
  "Materials": "Materiales",
  "Labor": "Mano de obra",
  "Plumbing": "Plomería",
  "Electrical": "Eléctrico",
  "Flooring": "Pisos",
  "Appliances": "Electrodomésticos",
  "Permits": "Permisos",
  "Other": "Otro",
};

const LangCtx = React.createContext({ lang: "en", t: (s) => s, setLang: () => {} });
const useT = () => React.useContext(LangCtx);
function makeT(lang) {
  return (s) => (lang === "es" && ES[s] ? ES[s] : s);
}

// Viewport hook — drives the mobile (single column, edge-to-edge) vs
// desktop (centered, widened, card grids) split, mirroring the Investor
// Tracker. 768px is the tablet breakpoint.
function useViewport() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return { width, isWide: width >= 560 };
}

/* ============================================================
   BeeNexa · Real Estate · Houses Project
   - Full-stack module: partners + houses persist via RealEstateController
     (Grails/Mongo). Houses carry their expenses & close-out settlement
     embedded, so a write upserts the whole ledger.
   - Categories stay client-side (localStorage) — they are cosmetic labels.
   - Session "logged in as" partner uses the same switch-user UX as the
     Investor Tracker: a pill button + a "Switch user" modal.
   ============================================================ */


const SEED_CATEGORIES = [
  { id: "purchase", label: "Purchase", emoji: "🏠" },
  { id: "paint", label: "Paint", emoji: "🎨" },
  { id: "repairs", label: "Repairs", emoji: "🔧" },
  { id: "materials", label: "Materials", emoji: "🧱" },
  { id: "labor", label: "Labor", emoji: "👷" },
  { id: "plumbing", label: "Plumbing", emoji: "🚰" },
  { id: "electrical", label: "Electrical", emoji: "💡" },
  { id: "flooring", label: "Flooring", emoji: "🪵" },
  { id: "appliances", label: "Appliances", emoji: "🧊" },
  { id: "permits", label: "Permits", emoji: "📄" },
  { id: "other", label: "Other", emoji: "📦" },
];

// Categories are cosmetic labels (emoji + name). They are not part of the
// houses ledger, so they persist client-side in localStorage rather than
// the backend — mirroring how the Investor Tracker keeps its editable
// expense-description list.
const RE_CAT_KEY = "realEstate.categories";
function loadCategories() {
  if (typeof window === "undefined") return SEED_CATEGORIES;
  try {
    const raw = window.localStorage.getItem(RE_CAT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch (e) { /* fall through to seed */ }
  return SEED_CATEGORIES;
}
function persistCategories(list) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(RE_CAT_KEY, JSON.stringify(list)); } catch (e) { /* ignore */ }
}

const findC = (categories, id) => categories.find((c) => c.id === id) || categories[categories.length - 1] || { label: "Other", emoji: "📦" };
const findP = (partners, id) => partners.find((p) => p.personId === id) || partners[0] || { name: "—", color: "#888" };
const tintOf = (hex) => hex + "22";
const uid = () => Math.random().toString(36).slice(2, 9);
const fmt = (n) =>
  "$" + (Math.round((n + Number.EPSILON) * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function evenSplit(partners) {
  const n = partners.length || 1;
  const base = Math.floor(100 / n);
  const o = {};
  partners.forEach((p) => (o[p.personId] = base));
  let r = 100 - base * n, k = 0;
  while (r > 0 && partners.length) { o[partners[k % n].personId] += 1; r--; k++; }
  return o;
}
function evenSplitIds(ids) { return evenSplit(ids.map((id) => ({ personId: id }))); }

function txPayments(t) { return t.payments || (t.paid ? [{ id: "legacy", amount: t.amount, date: t.paidAt, note: t.paidNote }] : []); }
function txPaid(t) { return Math.round(txPayments(t).reduce((s, p) => s + (Number(p.amount) || 0), 0) * 100) / 100; }
function txRemaining(t) { return Math.round((t.amount - txPaid(t)) * 100) / 100; }
function txSettled(t) { return txRemaining(t) <= 0.005; }

function holdingInfo(house) {
  const monthly = house.holdingMonthly || 0;
  if (!house.acquired) return { daysHeld: null, monthly, total: 0 };
  const start = new Date(house.acquired);
  const end = house.closed && house.closedAt ? new Date(house.closedAt) : new Date();
  const daysHeld = Math.max(0, Math.round((end - start) / 86400000));
  const months = daysHeld / 30.44;
  return { daysHeld, monthly, total: Math.round(monthly * months * 100) / 100 };
}

function splitByPct(total, splits, partners) {
  const cents = Math.round(total * 100);
  const raw = partners.map((p) => ({ id: p.personId, exact: (cents * (splits[p.personId] || 0)) / 100 }));
  const out = {};
  let used = 0;
  raw.forEach((rr) => { out[rr.id] = Math.floor(rr.exact); used += out[rr.id]; });
  let rem = cents - used;
  const order = raw.map((rr) => ({ id: rr.id, frac: rr.exact - Math.floor(rr.exact) })).sort((a, b) => b.frac - a.frac);
  let i = 0;
  while (rem > 0 && order.length) { out[order[i % order.length].id] += 1; rem--; i++; }
  const res = {};
  Object.keys(out).forEach((kk) => (res[kk] = out[kk] / 100));
  return res;
}

function moneySplit(amount, splits, partners) {
  const cents = Math.round(amount * 100);
  const out = {};
  let used = 0;
  const raw = partners.map((p) => ({ id: p.personId, exact: (cents * (splits[p.personId] || 0)) / 100 }));
  raw.forEach((r) => { out[r.id] = Math.trunc(r.exact); used += out[r.id]; });
  let rem = cents - used;
  const order = raw.map((r) => ({ id: r.id, frac: Math.abs(r.exact - Math.trunc(r.exact)) })).sort((a, b) => b.frac - a.frac);
  const step = rem >= 0 ? 1 : -1;
  rem = Math.abs(rem);
  let k = 0;
  while (rem > 0 && order.length) { out[order[k % order.length].id] += step; rem--; k++; }
  const res = {};
  Object.keys(out).forEach((kk) => (res[kk] = out[kk] / 100));
  return res;
}

/* ---------- deal economics (Change 1) ----------
   The full purchase price is a house field. Profit/equity are computed
   against the PROJECT COST (allIn = purchaseBasis + renoSpent), not just
   cash. Cash (total) still drives the "Total invested" hero, who-paid-what
   and the settlement — those stay on `total`.

     total         = sum of all expenses (CASH invested)
     purchaseCash  = sum of 'purchase'-category expenses (the down payment[s])
     renoSpent     = total - purchaseCash
     purchaseBasis = house.purchasePrice ?? purchaseCash   // null => old behaviour
     allIn         = purchaseBasis + renoSpent             // PROJECT COST basis
*/
function houseEconomics(house, exps) {
  const total = exps.reduce((s, e) => s + e.amount, 0);
  const purchaseCash = exps.filter((e) => e.category === "purchase").reduce((s, e) => s + e.amount, 0);
  const renoSpent = total - purchaseCash;
  const hasPrice = house && house.purchasePrice != null;
  const purchaseBasis = hasPrice ? house.purchasePrice : purchaseCash;
  const allIn = purchaseBasis + renoSpent;
  return { total, purchaseCash, renoSpent, purchaseBasis, allIn, hasPrice };
}

/* ---------- download / PDF helpers ---------- */
function downloadDataUrl(name, dataUrl) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name || "file";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) return resolve();
    const s = document.createElement("script");
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
}
async function ensureJsPDF() {
  if (window.jspdf && window.jspdf.jsPDF) return window.jspdf.jsPDF;
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
  return window.jspdf.jsPDF;
}
function loadImg(src) {
  return new Promise((resolve) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = () => resolve(null); i.src = src; });
}

async function housePDF(house, expenses, partners, categories) {
  const JsPDF = await ensureJsPDF();
  const doc = new JsPDF({ unit: "pt", format: "letter" });
  const PW = 612, PH = 792, M = 48; let y = M;
  const ensure = (need) => { if (y + need > PH - M) { doc.addPage(); y = M; } };
  const line = (txt, size = 10, bold = false, color = [20, 36, 28]) => {
    doc.setFont("helvetica", bold ? "bold" : "normal"); doc.setFontSize(size); doc.setTextColor(color[0], color[1], color[2]);
    ensure(size + 6); doc.text(String(txt), M, y); y += size + 6;
  };
  const kv = (label, value, bold = false) => {
    ensure(16); doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(110, 122, 113);
    doc.text(label, M, y);
    doc.setFont("helvetica", bold ? "bold" : "normal"); doc.setTextColor(20, 36, 28);
    doc.text(String(value), PW - M, y, { align: "right" }); y += 16;
  };
  const rule = () => { ensure(10); doc.setDrawColor(220); doc.line(M, y, PW - M, y); y += 10; };
  const heading = (t) => { y += 6; line(t, 12, true, [15, 81, 50]); };

  const { total, renoSpent, purchaseBasis, allIn, hasPrice } = houseEconomics(house, expenses);
  const statusLbl = house.closed ? "CLOSED" : house.settlement ? "CLOSING" : house.status === "completed" ? "COMPLETED" : "ACTIVE";

  line(house.name, 18, true);
  line(`${house.location || ""}   ·   ${statusLbl}   ·   Generated ${today()}`, 9, false, [120, 120, 120]);
  rule();

  heading("Deal economics");
  const hold = holdingInfo(house);
  if (house.arv != null) kv("ARV (after-repair value)", fmt(house.arv));
  kv(hasPrice ? "Purchase price (full)" : "Purchase (from expenses)", fmt(purchaseBasis));
  kv("Renovation (from expenses)", fmt(renoSpent));
  kv("All-in (project cost)", fmt(allIn), true);
  if (hasPrice) kv("Cash invested (expenses)", fmt(total));
  if (hold.daysHeld != null) kv("Days held", `${hold.daysHeld} days`);
  if (hold.monthly > 0) kv("Holding (est.)", `${fmt(hold.total)} (${fmt(hold.monthly)}/mo)`);
  if (house.budget) kv("Renovation budget", `${fmt(renoSpent)} spent of ${fmt(house.budget)}`);
  if (house.arv != null) kv("Projected profit", `${fmt(house.arv - allIn - hold.total)}  (${(((house.arv - allIn - hold.total) / house.arv) * 100).toFixed(0)}%)`, true);
  if (house.status === "completed" && house.salePrice != null) {
    const net = house.salePrice - allIn - (house.sellingCosts || 0) - hold.total;
    kv("Sale price", fmt(house.salePrice));
    kv("Selling costs", fmt(house.sellingCosts || 0));
    if (hold.total > 0) kv("Holding costs", fmt(hold.total));
    kv("Net profit", `${fmt(net)}  (ROI ${allIn ? ((net / allIn) * 100).toFixed(0) : 0}%)`, true);
  }

  heading("Who paid what");
  const shouldPay = splitByPct(total, house.splits, partners);
  partners.forEach((p) => {
    const paid = expenses.filter((e) => e.personId === p.personId).reduce((s, e) => s + e.amount, 0);
    const net = paid - (shouldPay[p.personId] || 0);
    kv(`${p.name} (${house.splits[p.personId] || 0}%)`, `paid ${fmt(paid)} · ${net >= 0 ? "+" : "-"}${fmt(Math.abs(net))}`);
  });

  heading(house.closed ? "Account closed" : "Who owes who");
  if (house.settlement) {
    if (house.closedAt) line(`Closed on ${house.closedAt}`, 9, false, [120, 120, 120]);
    house.settlement.transfers.forEach((t) => {
      kv(`${findP(partners, t.from).name} -> ${findP(partners, t.to).name}`,
        `${fmt(t.amount)}${txSettled(t) ? `  paid in full` : `  paid ${fmt(txPaid(t))} · ${fmt(txRemaining(t))} left`}`);
    });
  } else {
    line("Open — not reconciled yet.", 9, false, [120, 120, 120]);
  }

  heading(`Expenses (${expenses.length})`);
  const cols = { date: M, cat: M + 78, desc: M + 200, amt: PW - M };
  partners.forEach((p) => {
    const items = expenses.filter((e) => e.personId === p.personId).sort((a, b) => (a.date < b.date ? -1 : 1));
    if (items.length === 0) return;
    const sub = items.reduce((s, e) => s + e.amount, 0);
    y += 4;
    ensure(18);
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(20, 36, 28);
    doc.text(`${p.name}`, M, y);
    doc.text(`Total paid ${fmt(sub)}`, PW - M, y, { align: "right" }); y += 4;
    doc.setDrawColor(200); doc.line(M, y, PW - M, y); y += 12;
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(110, 122, 113);
    ensure(14);
    doc.text("Date", cols.date, y); doc.text("Category", cols.cat, y); doc.text("Description", cols.desc, y);
    doc.text("Amount", cols.amt, y, { align: "right" }); y += 12;
    items.forEach((e) => {
      ensure(14);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(40, 56, 47);
      doc.text(e.date || "", cols.date, y);
      doc.text(findC(categories, e.category).label, cols.cat, y);
      const d = (e.desc || "").slice(0, 24);
      doc.text(d + (e.receipts && e.receipts.length ? `  [${e.receipts.length}]` : ""), cols.desc, y);
      doc.text(fmt(e.amount), cols.amt, y, { align: "right" }); y += 13;
    });
    y += 4;
  });
  rule();
  kv("TOTAL (all partners)", fmt(total), true);

  const withImgs = expenses.filter((e) => (e.receipts || []).some((r) => r.type && r.type.startsWith("image/")));
  if (withImgs.length) {
    doc.addPage(); y = M; line("Receipts", 14, true);
    for (const e of withImgs) {
      for (const r of e.receipts.filter((x) => x.type && x.type.startsWith("image/"))) {
        const img = await loadImg(r.dataUrl);
        if (!img) continue;
        const maxW = PW - M * 2, maxH = 300;
        let w = img.naturalWidth, h = img.naturalHeight;
        const scale = Math.min(maxW / w, maxH / h, 1); w *= scale; h *= scale;
        ensure(h + 26);
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(40, 56, 47);
        doc.text(`${findC(categories, e.category).label} · ${fmt(e.amount)} · ${e.date}`, M, y); y += 6;
        const fmtType = r.type.includes("png") ? "PNG" : "JPEG";
        try { doc.addImage(r.dataUrl, fmtType, M, y, w, h); } catch (err) { /* skip unsupported */ }
        y += h + 18;
      }
    }
  }

  return doc.output("blob");
}

/* ---------- Calendar date picker (same UX as Loans / Investor Tracker,
   restyled to this module's emerald + paper palette) ---------- */
const DP_WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const DP_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DP_MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const parseISO = (iso) => {
  if (!iso) return null;
  const parts = String(iso).split("-");
  if (parts.length !== 3) return null;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return Number.isNaN(d.getTime()) ? null : d;
};
const toISODate = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
};
const dpSameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dpYearPage = (year) => { const start = year - (((year % 12) + 12) % 12); return [start, start + 11]; };

function DatePicker({ value, onChange, placeholder }) {
  const G = GREEN_SOLID, INK_C = INK, HAIR_C = HAIR, MUTED = "#6B7A70", HOVER = "#EAF5EE";
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("days"); // 'days' | 'months' | 'years'
  const [cursor, setCursor] = useState(() => {
    const init = parseISO(value) || new Date();
    return new Date(init.getFullYear(), init.getMonth(), 1);
  });
  const rootRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const openPicker = () => {
    const init = parseISO(value) || new Date();
    setCursor(new Date(init.getFullYear(), init.getMonth(), 1));
    setView("days"); setOpen(true);
  };
  const prevMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const prevYear = () => setCursor((c) => new Date(c.getFullYear() - 1, c.getMonth(), 1));
  const nextYear = () => setCursor((c) => new Date(c.getFullYear() + 1, c.getMonth(), 1));
  const prevYearPage = () => setCursor((c) => new Date(c.getFullYear() - 12, c.getMonth(), 1));
  const nextYearPage = () => setCursor((c) => new Date(c.getFullYear() + 12, c.getMonth(), 1));
  const selectMonth = (m) => { setCursor((c) => new Date(c.getFullYear(), m, 1)); setView("days"); };
  const selectYear = (yr) => { setCursor((c) => new Date(yr, c.getMonth(), 1)); setView("months"); };
  const selectDay = (day) => { onChange(toISODate(new Date(cursor.getFullYear(), cursor.getMonth(), day))); setOpen(false); };
  const selectToday = () => { const t = new Date(); onChange(toISODate(t)); setCursor(new Date(t.getFullYear(), t.getMonth(), 1)); setView("days"); setOpen(false); };
  const cycleView = () => setView((v) => (v === "days" ? "months" : v === "months" ? "years" : "days"));

  const selected = parseISO(value);
  const today = new Date();
  const firstWeekday = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const displayValue = selected ? selected.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  const navBtn = { background: "#F1EFE7", border: "none", width: 32, height: 32, borderRadius: 8, cursor: "pointer", color: INK_C, display: "flex", alignItems: "center", justifyContent: "center" };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button type="button" onClick={() => (open ? setOpen(false) : openPicker())}
        style={{ width: "100%", boxSizing: "border-box", height: 48, padding: "12px 14px", fontSize: 15, fontFamily: "inherit", textAlign: "left", border: `1px solid ${open ? G : HAIR_C}`, borderRadius: 13, background: "#FCFBF8", color: displayValue ? INK_C : MUTED, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{displayValue || placeholder || "Select date"}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4 M8 2v4 M3 10h18" /></svg>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: "#fff", borderRadius: 16, boxShadow: "0 16px 40px rgba(19,35,28,.22)", border: `1px solid ${HAIR_C}`, padding: 16, zIndex: 9100, fontFamily: "inherit" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button type="button" onClick={view === "days" ? prevMonth : view === "months" ? prevYear : prevYearPage} style={navBtn} aria-label="Previous">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button type="button" onClick={cycleView} style={{ background: "transparent", border: "none", fontSize: 15, fontWeight: 800, color: INK_C, fontFamily: "inherit", cursor: "pointer", padding: "4px 10px", borderRadius: 8, display: "flex", alignItems: "center", gap: 4 }} aria-label="Switch view">
              {view === "days" && `${DP_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
              {view === "months" && cursor.getFullYear()}
              {view === "years" && (() => { const [s, e] = dpYearPage(cursor.getFullYear()); return `${s} – ${e}`; })()}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d={view === "years" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} /></svg>
            </button>
            <button type="button" onClick={view === "days" ? nextMonth : view === "months" ? nextYear : nextYearPage} style={navBtn} aria-label="Next">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
          {view === "days" ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
                {DP_WEEKDAYS.map((w, i) => <div key={`${w}-${i}`} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.5, padding: "4px 0" }}>{w}</div>)}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                {cells.map((day, idx) => {
                  if (day === null) return <div key={`b-${idx}`} />;
                  const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
                  const isSel = dpSameDay(cellDate, selected);
                  const isTod = dpSameDay(cellDate, today);
                  let bg = "transparent", color = INK_C, fontWeight = 500, border = "1px solid transparent";
                  if (isSel) { bg = G; color = "#fff"; fontWeight = 800; }
                  else if (isTod) { border = `1px solid ${G}`; color = G; fontWeight = 800; }
                  return (
                    <button key={day} type="button" onClick={() => selectDay(day)}
                      style={{ background: bg, color, fontWeight, border, borderRadius: 10, height: 36, cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}
                      onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = HOVER; }}
                      onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}>{day}</button>
                  );
                })}
              </div>
            </>
          ) : view === "months" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {DP_MONTHS_SHORT.map((m, idx) => {
                const isCur = idx === cursor.getMonth();
                let bg = "transparent", color = INK_C, fontWeight = 500;
                if (isCur) { bg = G; color = "#fff"; fontWeight = 800; }
                return <button key={m} type="button" onClick={() => selectMonth(idx)} style={{ background: bg, color, fontWeight, border: "1px solid transparent", borderRadius: 10, height: 48, cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}
                  onMouseEnter={(e) => { if (!isCur) e.currentTarget.style.background = HOVER; }} onMouseLeave={(e) => { if (!isCur) e.currentTarget.style.background = "transparent"; }}>{m}</button>;
              })}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
              {(() => { const [startYr] = dpYearPage(cursor.getFullYear()); const years = []; for (let i = 0; i < 12; i += 1) years.push(startYr + i);
                return years.map((yr) => {
                  const isCur = yr === cursor.getFullYear();
                  let bg = "transparent", color = INK_C, fontWeight = 500;
                  if (isCur) { bg = G; color = "#fff"; fontWeight = 800; }
                  return <button key={yr} type="button" onClick={() => selectYear(yr)} style={{ background: bg, color, fontWeight, border: "1px solid transparent", borderRadius: 10, height: 48, cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}
                    onMouseEnter={(e) => { if (!isCur) e.currentTarget.style.background = HOVER; }} onMouseLeave={(e) => { if (!isCur) e.currentTarget.style.background = "transparent"; }}>{yr}</button>;
                }); })()}
            </div>
          )}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${HAIR_C}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button type="button" onClick={selectToday} style={{ background: "transparent", border: "none", color: G, fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: "pointer", padding: "6px 8px" }}>Today</button>
            <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: MUTED, fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "6px 8px" }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Toolbar icons (stroke style, matching Loans' IconPlus).
const IC = {
  back: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  plus: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>,
  circleAdd: <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" /></svg>,
  pdf: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
  edit: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
};

function RealEstate(props) {
  const {
    partners = [],
    houses: allHouses = [],
    addableUsers = [],
    currentPersonId,
    role,
    canSeeAll,
    listRealEstate,
    addPartnerUser,
    deletePartner,
    saveHouse,
    deleteHouse,
    saveExpense,
    deleteExpense,
  } = props;

  const { isWide } = useViewport();
  // Change 12 — UI language (in-memory; not persisted yet). makeT maps an
  // English source string to its Spanish translation when lang === 'es'.
  const [lang, setLang] = useState("en");
  const t = useMemo(() => makeT(lang), [lang]);
  const pdfActionRef = React.useRef(null); // HouseDetail registers its makePDF here so the toolbar can trigger it
  const [categories, setCategories] = useState(loadCategories);
  // Admin "view as" — preview the module from another partner's seat (same
  // pattern as the Investor Tracker). Non-admins can't switch; they are
  // locked to their own seat.
  const [viewAsPersonId, setViewAsPersonId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState("list");
  const [modal, setModal] = useState(null);
  // House create/edit is a full screen (not a dialog). null = closed,
  // { data: null } = new house, { data: house } = edit.
  const [houseForm, setHouseForm] = useState(null);
  const [toast, setToast] = useState(null);
  // Translate toast copy centrally; `prev` avoids shadowing the translator t.
  const showToast = (msg) => { const m = t(msg); setToast(m); setTimeout(() => setToast((prev) => (prev === m ? null : prev)), 1800); };
  const [filterPerson, setFilterPerson] = useState("all");
  const [filterCat, setFilterCat] = useState("all");

  // Pull partners + houses for this company on mount.
  useEffect(() => { listRealEstate({}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const realUserId = currentPersonId || (partners[0] && partners[0].personId) || null;
  const realIsAdmin = !!canSeeAll || role === "admin";
  // Effective seat: the partner whose perspective we render and to whom new
  // expenses are attributed. Admins may view as someone else.
  const currentUserId = (realIsAdmin && viewAsPersonId) || realUserId;
  const effPartner = partners.find((p) => p.personId === currentUserId);
  const isAdmin = effPartner ? effPartner.role === "admin" : realIsAdmin;
  const isImpersonating = realIsAdmin && !!viewAsPersonId && viewAsPersonId !== realUserId;
  const switchUser = (id) => { setViewAsPersonId(id === realUserId ? null : id); setOpenId(null); setView("list"); setHouseForm(null); };

  // Admins see every house; partners (and an admin previewing as a partner)
  // see only the houses they belong to. Backend already scopes for
  // partners; this also drives the admin's "view as" preview.
  const houses = useMemo(
    () => (isAdmin ? allHouses : allHouses.filter((h) => (h.members || []).includes(currentUserId))),
    [allHouses, isAdmin, currentUserId],
  );

  // Expenses are embedded on each house server-side; flatten them into one
  // list (tagged with houseId) so the presentational components below keep
  // working unchanged.
  const expenses = useMemo(
    () => houses.flatMap((h) => (h.expenses || []).map((e) => ({ ...e, houseId: h.id }))),
    [houses],
  );

  const houseTotal = (hid) => expenses.filter((e) => e.houseId === hid).reduce((s, e) => s + e.amount, 0);
  const houseCount = (hid) => expenses.filter((e) => e.houseId === hid).length;
  // Project-cost basis per house (Change 1) — drives card/list profit & equity.
  const econOf = (h) => houseEconomics(h, expenses.filter((e) => e.houseId === h.id));
  const open = houses.find((h) => h.id === openId) || null;
  const findHouse = (hid) => houses.find((h) => h.id === hid);

  const saveHouseFn = (data) => {
    saveHouse(data, (err) => { if (!err) { setHouseForm(null); showToast(data.id ? "Changes saved ✓" : "House created ✓"); } });
  };
  const deleteHouseFn = (hid) => {
    deleteHouse({ id: hid }, (err) => { if (!err) { setOpenId(null); setHouseForm(null); showToast("House deleted"); } });
  };
  const saveExpenseFn = (data) => {
    const isEdit = !!data.id;
    const expense = isEdit ? data : { ...data, id: uid(), personId: currentUserId };
    saveExpense({ houseId: data.houseId, expense }, (err) => {
      if (!err) { setModal(null); showToast(isEdit ? "Changes saved ✓" : "Expense added ✓"); }
    });
  };
  const deleteExpenseFn = (eid) => {
    const exp = expenses.find((e) => e.id === eid);
    if (!exp) return;
    deleteExpense({ houseId: exp.houseId, expenseId: eid }, (err) => { if (!err) { setModal(null); showToast("Expense deleted"); } });
  };

  const removePartner = (id) => {
    deletePartner({ personId: id }, (err) => { if (!err) { if (viewAsPersonId === id) setViewAsPersonId(null); showToast("Access revoked ✓"); } });
  };
  // Grant a company user Real Estate access (becomes a partner) directly.
  const grantPartner = (userId) => {
    addPartnerUser({ userId }, (err) => { if (!err) showToast("Partner added ✓"); });
  };
  const addMemberToHouse = (hid, personId) => {
    const h = findHouse(hid);
    if (!h) return;
    const mem = h.members || partners.map((p) => p.personId);
    if (mem.includes(personId)) return;
    const nm = [...mem, personId];
    saveHouse({ ...h, members: nm, splits: evenSplitIds(nm) }, (err) => { if (!err) showToast("Partner added ✓"); });
  };

  const addCategory = (label, emoji) => {
    const id = "c-" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 16) + "-" + uid().slice(0, 3);
    const c = { id, label: label.trim(), emoji: emoji || "🏷️" };
    setCategories((cs) => { const next = [...cs, c]; persistCategories(next); return next; });
    return id;
  };

  // Close-out / settlement changes mutate the house's settlement|closed
  // fields and are persisted by upserting the whole house ledger.
  const closeAccount = (hid, transfers) => {
    const h = findHouse(hid);
    if (!h) return;
    saveHouse({ ...h, status: "completed", settlement: { transfers, createdAt: today() } }, (err) => { if (!err) showToast("Account closing — confirm payments"); });
  };
  const addPayment = (hid, tid, pay) => {
    const h = findHouse(hid);
    if (!h || !h.settlement) return;
    const settlement = { ...h.settlement, transfers: h.settlement.transfers.map((tx) => (tx.id === tid ? { ...tx, payments: [...(tx.payments || []), { ...pay, id: uid() }] } : tx)) };
    saveHouse({ ...h, settlement }, (err) => { if (!err) showToast("Payment recorded ✓"); });
  };
  const removePayment = (hid, tid, pid) => {
    const h = findHouse(hid);
    if (!h || !h.settlement) return;
    const settlement = { ...h.settlement, transfers: h.settlement.transfers.map((tx) => (tx.id === tid ? { ...tx, payments: (tx.payments || []).filter((p) => p.id !== pid) } : tx)) };
    saveHouse({ ...h, settlement });
  };
  const finalizeClose = (hid) => {
    const h = findHouse(hid);
    if (!h) return;
    saveHouse({ ...h, closed: true, closedAt: today() }, (err) => { if (!err) { setOpenId(null); showToast("Account closed ✓"); } });
  };
  const reopenAccount = (hid) => {
    const h = findHouse(hid);
    if (!h) return;
    saveHouse({ ...h, settlement: null, closed: false, closedAt: null }, (err) => { if (!err) showToast("Account reopened"); });
  };

  const formOpen = houseForm !== null;
  const editingHouse = formOpen && !!houseForm.data;

  // Contextual toolbar (back + title) — same pattern as the Investor
  // Tracker: a back arrow + title on every drill-in screen, and a "+"
  // create action on the list.
  let toolbarTitle = t("Houses Project");
  let onToolbarBack = null;
  let onToolbarCreate = null;
  if (formOpen) {
    toolbarTitle = editingHouse ? t("Edit House") : t("New House");
    onToolbarBack = () => setHouseForm(null);
  } else if (open) {
    toolbarTitle = open.name || t("House");
    onToolbarBack = () => { setOpenId(null); setFilterPerson("all"); setFilterCat("all"); };
  } else if (view === "portfolio") {
    toolbarTitle = t("Portfolio Summary");
    onToolbarBack = () => setView("list");
  } else {
    // Anyone with the module can create a house (they become a member).
    onToolbarCreate = () => setHouseForm({ data: null });
  }
  // Toolbar left actions: Back when drilled in (with Edit + PDF on the
  // house detail). Creating a house is the bottom-right FAB (Loans style),
  // not a toolbar action.
  let headerButtons = [];
  if (onToolbarBack) {
    headerButtons = [{ icon: IC.back, title: t("Back"), handler: onToolbarBack }];
    if (open && !formOpen) {
      headerButtons.push({ icon: IC.edit, title: t("Edit"), handler: () => setHouseForm({ data: open }) });
      headerButtons.push({ icon: IC.pdf, title: t("PDF"), handler: () => pdfActionRef.current && pdfActionRef.current() });
    }
  } else if (onToolbarCreate && isWide) {
    // Desktop: circle-add in the toolbar. Mobile uses the bottom-right FAB.
    headerButtons = [{ icon: IC.circleAdd, title: t("New House"), handler: onToolbarCreate }];
  }

  // Full-bleed content on every screen — matches the Investor Tracker (no
  // centered narrow column, no side gaps, uniform paper background).
  const contentStyle = { width: "100%", background: PAPER, minHeight: "calc(100vh - 64px)", paddingBottom: 96 };

  return (
    <LangCtx.Provider value={{ lang, setLang, t }}>
    <div style={S.app}>
      <CustomToolbar
        title={toolbarTitle}
        headerButtons={headerButtons}
      />

      {/* Always-visible language switcher (Change 12) */}
      <div style={S.langBar}><LangSwitcher /></div>

      <div style={contentStyle}>
          {formOpen ? (
            <HouseForm data={houseForm.data} partners={partners} isWide={isWide}
              selfId={currentUserId}
              onSave={saveHouseFn} onDelete={deleteHouseFn} onClose={() => setHouseForm(null)} />
          ) : (
            <>
              {realIsAdmin && (
                <SessionBar partners={partners} currentUserId={currentUserId} realUserId={realUserId}
                  isImpersonating={isImpersonating} canSwitch={realIsAdmin} isAdmin={isAdmin}
                  onSwitch={switchUser} onPartners={() => setModal({ type: "partners" })} />
              )}

              {open ? (
                <HouseDetail house={open} partners={partners} categories={categories} currentUserId={currentUserId} isWide={isWide} isAdmin={isAdmin} pdfRef={pdfActionRef}
                  expenses={expenses.filter((e) => e.houseId === open.id)}
                  filterPerson={filterPerson} filterCat={filterCat}
                  setFilterPerson={setFilterPerson} setFilterCat={setFilterCat}
                  onEditHouse={() => setHouseForm({ data: open })}
                  onAddExpense={() => setModal({ type: "expense", data: { houseId: open.id } })}
                  onEditExpense={(e) => setModal({ type: "expense", data: e })}
                  onCloseAccount={closeAccount} onAddPayment={addPayment} onRemovePayment={removePayment}
                  onFinalize={finalizeClose} onReopen={reopenAccount}
                  onAddMember={() => setModal({ type: "member", data: { houseId: open.id } })} />
              ) : view === "portfolio" ? (
                <PortfolioSummary houses={houses} expenses={expenses} partners={partners} isWide={isWide}
                  onOpen={setOpenId} />
              ) : (
                <HousesList houses={houses} houseTotal={houseTotal} houseCount={houseCount} econOf={econOf} isWide={isWide}
                  onOpen={setOpenId} onAdd={() => setHouseForm({ data: null })}
                  onPortfolio={() => setView("portfolio")} />
              )}
            </>
          )}
      </div>

      {modal?.type === "expense" && (
        <ExpenseForm data={modal.data} partners={partners} categories={categories} addCategory={addCategory} currentUserId={currentUserId}
          onSave={saveExpenseFn} onDelete={deleteExpenseFn} onClose={() => setModal(null)} />
      )}
      {modal?.type === "partners" && (
        <PartnersForm partners={partners} expenses={expenses} addableUsers={addableUsers} onAddUser={grantPartner} onRemove={removePartner} onClose={() => setModal(null)} />
      )}
      {modal?.type === "member" && open && (
        <AddMemberForm house={open} partners={partners}
          onAddExisting={(id) => addMemberToHouse(open.id, id)}
          onClose={() => setModal(null)} />
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
    </LangCtx.Provider>
  );
}

/* Language switcher (Change 12) — a globe pill that opens a dropdown of
   EN / ES with flags and a checkmark on the active language. A full-screen
   invisible backdrop dismisses it on an outside tap. */
function LangSwitcher() {
  const { lang, setLang } = useT();
  const [open, setOpen] = useState(false);
  const cur = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <div style={{ position: "relative" }}>
      <button style={S.langBtn} onClick={() => setOpen((o) => !o)} aria-label="Language">
        <span style={{ fontSize: 14 }}>🌐</span>
        <span>{cur.flag} {cur.code.toUpperCase()}</span>
        <span style={{ opacity: 0.6, fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <>
          <div style={S.langBackdrop} onClick={() => setOpen(false)} />
          <div style={S.langMenu}>
            {LANGS.map((l) => (
              <button key={l.code} style={S.langItem} onClick={() => { setLang(l.code); setOpen(false); }}>
                <span style={{ fontSize: 16 }}>{l.flag}</span>
                <span style={{ flex: 1, textAlign: "left", fontWeight: lang === l.code ? 800 : 600 }}>{l.label}</span>
                {lang === l.code && <span style={{ color: "#16a34a", fontWeight: 800 }}>✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* Session bar — mirrors the Investor Tracker switch-user UX: a pill
   showing "Logged in as • Name ▾" that opens a Switch-user modal. New
   expenses are auto-attributed to the selected session partner. */
function SessionBar({ partners, currentUserId, realUserId, isImpersonating, canSwitch, isAdmin, onSwitch, onPartners }) {
  const { t } = useT();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const me = findP(partners, currentUserId);
  return (
    <div style={S.session}>
      <span style={S.sessionLbl}>{t("Logged in as")}</span>
      {canSwitch ? (
        <button style={S.sessionPill} onClick={() => setSwitcherOpen(true)}>
          <span style={{ ...S.dot, background: isImpersonating ? "#E4CE92" : me.color }} />
          <span style={S.sessionPillName}>{me.name}</span>
          <span style={S.sessionCaret}>▾</span>
        </button>
      ) : (
        <span style={S.sessionStatic}>
          <span style={{ ...S.dot, background: me.color }} />
          <span style={S.sessionPillName}>{me.name}</span>
        </span>
      )}
      {isImpersonating && <span style={S.previewChip}>{t("PREVIEW")}</span>}
      {isAdmin && <button style={S.sessionBtn} onClick={onPartners}>{t("Partners")}</button>}
      {switcherOpen && (
        <UserSwitcherModal partners={partners} currentUserId={currentUserId} realUserId={realUserId}
          onSelect={(id) => { onSwitch(id); setSwitcherOpen(false); }}
          onClose={() => setSwitcherOpen(false)} />
      )}
    </div>
  );
}

/* Switch-user modal — same interaction language as the Investor
   Tracker's UserSwitcherModal: avatar + name rows, the active partner
   highlighted with an ACTIVE chip. */
function UserSwitcherModal({ partners, currentUserId, realUserId, onSelect, onClose }) {
  const { t } = useT();
  const sorted = [...partners].sort((a, b) => {
    if (a.personId === realUserId) return -1;
    if (b.personId === realUserId) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });
  return (
    <Modal title={t("Switch user")} onClose={onClose}>
      <div style={S.switchHint}>{t("Preview the module from another partner's seat")}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sorted.map((p) => {
          const isActive = p.personId === currentUserId;
          const isReal = p.personId === realUserId;
          return (
            <button key={p.personId} onClick={() => onSelect(p.personId)}
              style={{ ...S.switchRow, ...(isActive ? S.switchRowOn : {}) }}>
              <span style={{ ...S.switchAvatar, background: p.color }}>{(p.name || "?").charAt(0).toUpperCase()}</span>
              <span style={S.switchName}>{p.name}<span style={S.switchRole}> · {p.role === "admin" ? t("Admin") : t("Partner")}</span></span>
              {isReal && <span style={S.switchActive}>{t("YOU")}</span>}
              {isActive && !isReal && <span style={{ ...S.switchActive, background: "#9A6B12", color: "#FBF0DD" }}>{t("PREVIEW")}</span>}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

function HousesList({ houses, houseTotal, houseCount, econOf, onOpen, onAdd, onPortfolio, isWide }) {
  const { t } = useT();
  const [tab, setTab] = useState("active");
  const activeCount = houses.filter((h) => !h.closed).length;
  const closedCount = houses.filter((h) => h.closed).length;
  const shown = houses.filter((h) => (tab === "closed" ? h.closed : !h.closed));
  const grand = shown.reduce((s, h) => s + houseTotal(h.id), 0);
  return (
    <>
      <div style={S.header}>
        <div><div style={S.eyebrow}>{t("Real Estate")}</div><div style={S.h1}>{t("Houses Project")}</div></div>
        <button style={S.portfolioBtn} onClick={onPortfolio}>📊<span style={S.portfolioBtnLbl}>{t("Portfolio")}</span></button>
      </div>
      <div style={S.statStrip}>
        <Mini label={tab === "closed" ? t("Closed") : t("Active houses")} value={shown.length} />
        <Mini label={t("Total invested")} value={fmt(grand)} />
      </div>

      <div style={S.tabBar}>
        <button style={{ ...S.tab, ...(tab === "active" ? S.tabOn : {}) }} onClick={() => setTab("active")}>{t("Active")} ({activeCount})</button>
        <button style={{ ...S.tab, ...(tab === "closed" ? S.tabOn : {}) }} onClick={() => setTab("closed")}>{t("Closed")} ({closedCount})</button>
      </div>

      <div style={S.body}>
        {shown.length === 0 && <Empty text={tab === "closed" ? t("No closed accounts yet.") : t("No active houses. Tap + New House to start.")} />}
        <div style={S.cardGrid}>
        {shown.map((h) => {
          const tot = houseTotal(h.id);
          const { allIn } = econOf(h);
          const hold = holdingInfo(h);
          const rent = (h.dealType || "flip") === "rent";
          const sold = h.status === "completed" && h.salePrice != null;
          const profit = sold ? h.salePrice - allIn - (h.sellingCosts || 0) - hold.total
            : rent ? (h.arv != null ? h.arv - allIn : null)
            : h.arv != null ? h.arv - allIn - hold.total : null;
          const profitLbl = sold ? t("Net profit") : rent ? t("Est. equity") : t("Est. profit");
          return (
            <button key={h.id} style={S.houseCard} onClick={() => onOpen(h.id)}>
              <div style={S.houseHeadRow}>
                <div style={S.houseName}>{h.name}</div>
                <span style={{ ...S.badge, ...(h.closed ? S.badgeClosed : h.settlement ? S.badgeClosing : h.status === "completed" ? S.badgeDone : S.badgeActive) }}>
                  {h.closed ? t("Closed") : h.settlement ? t("Closing") : h.status === "completed" ? t("Completed") : t("Active")}
                </span>
              </div>
              <div style={S.houseLoc}>📍 {h.location || "—"}{rent ? ` · 🔑 ${t("Rental")}` : ""}</div>
              <div style={S.houseFootRow}>
                <span style={S.houseTotal}>{fmt(tot)}</span>
                <span style={S.houseMeta}>{houseCount(h.id)} {t("expenses")}</span>
              </div>
              {profit != null && (
                <div style={S.cardProfitRow}>
                  <span style={S.cardProfitLbl}>{profitLbl}</span>
                  <span style={{ ...S.cardProfitVal, color: profit >= 0 ? "#16a34a" : "#dc2626" }}>{fmt(profit)}</span>
                </div>
              )}
            </button>
          );
        })}
        </div>
      </div>
      {!isWide && (
        <button style={S.fabRound} onClick={onAdd} aria-label="New House">{IC.plus}</button>
      )}
    </>
  );
}

/* =================== PORTFOLIO SUMMARY =================== */
function PortfolioSummary({ houses, expenses, partners, onOpen, isWide }) {
  const { t } = useT();
  const [expPartner, setExpPartner] = useState(null);
  const houseTotal = (hid) => expenses.filter((e) => e.houseId === hid).reduce((s, e) => s + e.amount, 0);
  // Est. profit uses the project-cost basis (Change 1): allIn = purchasePrice
  // (or down-payment fallback) + reno. Capital figures below stay on cash.
  const estProfit = (h) => {
    const { allIn } = houseEconomics(h, expenses.filter((e) => e.houseId === h.id));
    const hold = holdingInfo(h);
    const sold = h.status === "completed" && h.salePrice != null;
    if (sold) return h.salePrice - allIn - (h.sellingCosts || 0) - hold.total;
    if ((h.dealType || "flip") === "rent") return h.arv != null ? h.arv - allIn : null;
    if (h.arv != null) return h.arv - allIn - hold.total;
    return null;
  };
  const isClosed = (h) => !!h.closed;
  const memberIds = (h) => h.members || partners.map((p) => p.personId);

  const active = houses.filter((h) => !isClosed(h));
  const closed = houses.filter(isClosed);
  const totalInvested = houses.reduce((s, h) => s + houseTotal(h.id), 0);
  const activeCapital = active.reduce((s, h) => s + houseTotal(h.id), 0);
  const closedCapital = closed.reduce((s, h) => s + houseTotal(h.id), 0);
  const totalEstProfit = houses.reduce((s, h) => s + (estProfit(h) || 0), 0);

  // Per-partner roll-up across all houses
  const partnerRows = partners.map((p) => {
    let contributed = 0, atRisk = 0, net = 0, profitShare = 0, participates = false;
    houses.forEach((h) => {
      const tot = houseTotal(h.id);
      const paid = expenses.filter((e) => e.houseId === h.id && e.personId === p.personId).reduce((s, e) => s + e.amount, 0);
      const isMember = memberIds(h).includes(p.personId);
      if (paid > 0 || isMember) participates = true;
      contributed += paid;
      if (!isClosed(h)) atRisk += paid;
      const should = splitByPct(tot, h.splits || {}, partners)[p.personId] || 0;
      net += paid - should;
      const ep = estProfit(h);
      if (ep != null) profitShare += moneySplit(ep, h.splits || {}, partners)[p.personId] || 0;
    });
    return { p, contributed, atRisk, net: Math.round(net * 100) / 100, profitShare, participates };
  }).filter((r) => r.participates);

  // Profit by partner across all houses, with per-house breakdown
  const profitByPartner = partners.map((p) => {
    const byHouse = [];
    let total = 0;
    houses.forEach((h) => {
      const ep = estProfit(h);
      if (ep == null || !memberIds(h).includes(p.personId)) return;
      const share = moneySplit(ep, h.splits || {}, partners)[p.personId] || 0;
      total += share;
      byHouse.push({ id: h.id, name: h.name, closed: isClosed(h), share });
    });
    return { p, total, byHouse };
  }).filter((r) => r.byHouse.length > 0);
  const grandProfit = profitByPartner.reduce((s, r) => s + r.total, 0);

  return (
    <>
      <div style={S.body}>
        <div style={S.h1}>{t("Portfolio Summary")}</div>
        <div style={{ ...S.houseLoc, marginBottom: 14 }}>{t("Across")} {houses.length} {houses.length === 1 ? t("property") : t("properties")}</div>

        {/* Top KPI cards */}
        <div style={S.kpiGrid}>
          <div style={S.kpiCard}><div style={S.kpiLabel}>{t("Active Houses")}</div><div style={S.kpiValue}>{active.length}</div></div>
          <div style={S.kpiCard}><div style={S.kpiLabel}>{t("Closed Houses")}</div><div style={S.kpiValue}>{closed.length}</div></div>
          <div style={S.kpiCard}><div style={S.kpiLabel}>{t("Total Invested")}</div><div style={S.kpiValueSm}>{fmt(totalInvested)}</div></div>
          <div style={S.kpiCard}><div style={S.kpiLabel}>{t("Est. Profit")}</div><div style={{ ...S.kpiValueSm, color: totalEstProfit >= 0 ? "#16a34a" : "#dc2626" }}>{fmt(totalEstProfit)}</div></div>
        </div>

        {/* Capital Summary */}
        <div style={S.sectionLabel}>{t("Capital Summary")}</div>
        <div style={S.dealCard}>
          <DealRow label={t("Total Invested")} value={fmt(totalInvested)} big />
          <div style={S.dealDivide} />
          <DealRow label={t("Active Capital")} value={fmt(activeCapital)} />
          <DealRow label={t("Closed Capital")} value={fmt(closedCapital)} />
          <DealRow label={t("Capital at Risk")} value={fmt(activeCapital)} color="#b45309" />
          <div style={{ ...S.houseLoc, marginTop: 4 }}>{t("Capital at Risk = money in active deals, not yet sold.")}</div>
        </div>

        {/* Profit by Partner — all houses */}
        <div style={S.sectionLabel}>{t("Profit by Partner — all houses")}</div>
        {profitByPartner.length === 0 ? (
          <Empty text={t("No estimated profit yet. Add ARV or a sale price to a house.")} />
        ) : (
          <div style={S.dealCard}>
            {profitByPartner.map(({ p, total, byHouse }) => {
              const open = expPartner === p.personId;
              return (
                <div key={p.personId} style={S.ppWrap}>
                  <button style={S.ppRow} onClick={() => setExpPartner(open ? null : p.personId)}>
                    <span style={{ ...S.dot, background: p.color }} />
                    <span style={S.ppName}>{p.name}</span>
                    <span style={{ ...S.ppTotal, color: total >= 0 ? "#16a34a" : "#dc2626" }}>{fmt(total)}</span>
                    <span style={S.ppChevron}>{open ? "▴" : "▾"}</span>
                  </button>
                  {open && (
                    <div style={S.ppBreak}>
                      {byHouse.map((b) => (
                        <div key={b.id} style={S.ppBreakRow}>
                          <span style={S.ppBreakName}>{b.name} <span style={S.portPartnerPct}>{b.closed ? t("Closed") : t("Active")}</span></span>
                          <span style={{ ...S.ppBreakAmt, color: b.share >= 0 ? "#16a34a" : "#dc2626" }}>{fmt(b.share)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={S.ppGrand}>
              <span style={S.ppGrandLbl}>{t("Total estimated profit")}</span>
              <span style={{ ...S.ppGrandVal, color: grandProfit >= 0 ? "#16a34a" : "#dc2626" }}>{fmt(grandProfit)}</span>
            </div>
          </div>
        )}

        {/* Property list */}
        <div style={S.sectionLabel}>{t("Properties")}</div>
        {houses.length === 0 && <Empty text={t("No properties yet.")} />}
        <div style={S.cardGrid}>
        {houses.map((h) => {
          const tot = houseTotal(h.id);
          const ep = estProfit(h);
          const cl = isClosed(h);
          const mems = memberIds(h).map((id) => findP(partners, id)).filter(Boolean);
          const shares = ep != null ? moneySplit(ep, h.splits || {}, partners) : null;
          return (
            <button key={h.id} style={S.portRow} onClick={() => onOpen(h.id)}>
              <div style={S.portRowTop}>
                <span style={S.portName}>{h.name}</span>
                <span style={{ ...S.badge, ...(cl ? S.badgeClosed : S.badgeActive) }}>{cl ? t("Closed") : t("Active")}</span>
              </div>
              <div style={S.portLoc}>📍 {h.location || "—"}{(h.dealType || "flip") === "rent" ? ` · 🔑 ${t("Rental")}` : ""}</div>
              <div style={S.portStatsRow}>
                <span style={S.portStat}><span style={S.portStatLbl}>{t("Total Invested")}</span>{fmt(tot)}</span>
                <span style={S.portStat}><span style={S.portStatLbl}>{(h.dealType || "flip") === "rent" ? t("Est. Equity") : t("Est. Profit")}</span><b style={{ color: ep == null ? "#8a978f" : ep >= 0 ? "#16a34a" : "#dc2626" }}>{ep == null ? "—" : fmt(ep)}</b></span>
              </div>
              {shares && mems.length > 0 && (
                <div style={S.portPartners}>
                  <div style={S.portPartnersLbl}>{t("Profit by Partner")}</div>
                  {mems.map((m) => (
                    <div key={m.personId} style={S.portPartnerRow}>
                      <span style={{ ...S.dot, background: m.color }} />
                      <span style={S.portPartnerName}>{m.name} <span style={S.portPartnerPct}>{h.splits?.[m.personId] || 0}%</span></span>
                      <span style={{ ...S.portPartnerAmt, color: (shares[m.personId] || 0) >= 0 ? "#16a34a" : "#dc2626" }}>{fmt(shares[m.personId] || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </button>
          );
        })}
        </div>

        {/* Partner Summary */}
        <div style={S.sectionLabel}>{t("Partner Summary")}</div>
        {partnerRows.length === 0 && <Empty text={t("No partner activity yet.")} />}
        <div style={S.cardGrid}>
        {partnerRows.map(({ p, contributed, atRisk, net, profitShare }) => (
          <div key={p.personId} style={S.partnerCard}>
            <div style={S.partnerCardHead}>
              <span style={{ ...S.dot, background: p.color }} />
              <span style={{ ...S.partnerCardName, color: p.color }}>{p.name}</span>
            </div>
            <DealRow label={t("Total Contributed")} value={fmt(contributed)} />
            <DealRow label={net >= 0 ? t("To Receive (settle-up)") : t("Owes (settle-up)")} value={fmt(Math.abs(net))} color={net >= 0 ? "#16a34a" : "#dc2626"} />
            <DealRow label={t("Est. Profit Share")} value={fmt(profitShare)} color={profitShare >= 0 ? "#16a34a" : "#dc2626"} />
            <DealRow label={t("Capital at Risk")} value={fmt(atRisk)} color="#b45309" />
          </div>
        ))}
        </div>
        <div style={{ height: 24 }} />
      </div>
    </>
  );
}

function HouseDetail({
  house, partners, categories, currentUserId, expenses, filterPerson, filterCat,
  setFilterPerson, setFilterCat, isWide, isAdmin, pdfRef, onEditHouse, onAddExpense, onEditExpense,
  onCloseAccount, onAddPayment, onRemovePayment, onFinalize, onReopen, onAddMember,
}) {
  const { t } = useT();
  const members = (house.members || partners.map((p) => p.personId)).map((id) => findP(partners, id)).filter(Boolean);
  const closing = !!house.settlement && !house.closed;
  const closed = !!house.closed;
  const locked = closing || closed;
  const [payingId, setPayingId] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(today());
  const [payNote, setPayNote] = useState("");
  const [viewReceipts, setViewReceipts] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const pdfName = `${(house.name || "house").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
  const makePDF = async () => {
    setReportOpen(true);
    setPdfBusy(true);
    try {
      const blob = await housePDF(house, expenses, members, categories);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) { setPdfUrl(null); }
    setPdfBusy(false);
  };
  const closePdf = () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); setPdfUrl(null); setReportOpen(false); };
  // Expose the PDF trigger so the contextual toolbar (rendered by the
  // parent) can fire it.
  useEffect(() => {
    if (pdfRef) pdfRef.current = makePDF;
    return () => { if (pdfRef) pdfRef.current = null; };
  });
  const startPay = (tx) => { setPayingId(tx.id); setPayAmount(String(txRemaining(tx))); setPayDate(today()); setPayNote(""); };
  const confirmPay = (tid) => {
    const amt = Number(payAmount);
    if (!(amt > 0)) return;
    onAddPayment(house.id, tid, { amount: amt, date: payDate, note: payNote.trim() });
    setPayingId(null);
  };
  // Cash basis (total) drives the hero / who-paid-what / settlement; the
  // project-cost basis (allIn) drives profit & equity (Change 1).
  const { total, renoSpent, purchaseBasis, allIn, hasPrice } = houseEconomics(house, expenses);
  const hasArv = house.arv != null;
  const holding = holdingInfo(house);
  const isRent = (house.dealType || "flip") === "rent";
  const equity = hasArv ? house.arv - allIn : null;
  const equityMargin = hasArv && house.arv ? (equity / house.arv) * 100 : null;
  const cashFlow = house.monthlyRent != null ? house.monthlyRent - (house.holdingMonthly || 0) : null;
  const projProfit = hasArv ? house.arv - allIn - holding.total : null;
  const projMargin = hasArv && house.arv ? (projProfit / house.arv) * 100 : null;
  const hasBudget = house.budget != null && house.budget > 0;
  const budgetPct = hasBudget ? Math.min(100, (renoSpent / house.budget) * 100) : 0;
  const budgetDiff = hasBudget ? house.budget - renoSpent : 0;
  const sold = house.status === "completed" && house.salePrice != null;
  const sellCosts = house.sellingCosts || 0;
  const netProfit = sold ? house.salePrice - allIn - sellCosts - holding.total : null;
  const roi = sold && allIn ? (netProfit / allIn) * 100 : null;
  const profitShare = sold ? moneySplit(netProfit, house.splits, members) : null;
  const paid = useMemo(() => {
    const m = {}; members.forEach((p) => (m[p.personId] = 0));
    expenses.forEach((e) => (m[e.personId] = (m[e.personId] || 0) + e.amount));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expenses, house.members]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shouldPay = useMemo(() => splitByPct(total, house.splits, members), [total, house.splits, house.members]);
  const nets = members.map((p) => ({
    ...p, paid: paid[p.personId] || 0, should: shouldPay[p.personId] || 0,
    net: (paid[p.personId] || 0) - (shouldPay[p.personId] || 0),
  }));
  const settlements = useMemo(() => {
    const debt = nets.filter((n) => n.net < -0.005).map((n) => ({ ...n, rem: -n.net })).sort((a, b) => b.rem - a.rem);
    const cred = nets.filter((n) => n.net > 0.005).map((n) => ({ ...n, rem: n.net })).sort((a, b) => b.rem - a.rem);
    const out = []; let i = 0, j = 0;
    while (i < debt.length && j < cred.length) {
      const amt = Math.min(debt[i].rem, cred[j].rem);
      out.push({ from: debt[i].personId, to: cred[j].personId, amount: Math.round(amt * 100) / 100 });
      debt[i].rem -= amt; cred[j].rem -= amt;
      if (debt[i].rem < 0.005) i++; if (cred[j].rem < 0.005) j++;
    }
    return out;
  }, [nets]);
  const byCat = useMemo(() => {
    const m = {}; expenses.forEach((e) => (m[e.category] = (m[e.category] || 0) + e.amount));
    return Object.entries(m).map(([id, amt]) => ({ id, amt })).sort((a, b) => b.amt - a.amt);
  }, [expenses]);
  const list = [...expenses].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <>
      <div style={{ ...S.detailTitleWrap, paddingTop: 16 }}>
        <div style={S.h1}>{house.name} {closed ? "🔒" : ""}</div>
        <div style={S.houseLoc}>📍 {house.location || "—"}{closing ? ` · ${t("Closing")} — ${t("verifying payments")}` : closed ? ` · ${t("Account closed")}` : ""}</div>
      </div>

      <div style={S.hero}>
        <div style={S.heroLabel}>{t("Total invested")}</div>
        <div style={S.heroValue}>{fmt(total)}</div>
        <div style={S.heroSub}>{expenses.length} {t("expenses")} · {members.map((p) => `${p.name} ${house.splits[p.personId] || 0}%`).join(" · ")}</div>
      </div>

      <div style={S.body}>
        <div style={S.sectionLabel}>{t("Deal")}</div>
        <div style={S.dealCard}>
          <DealRow label={t("ARV (target value)")} value={hasArv ? fmt(house.arv) : t("— add in Edit")} />
          <div style={S.dealDivide} />
          <DealRow label={hasPrice ? t("Purchase price (full)") : t("Purchase (from expenses)")} value={fmt(purchaseBasis)} />
          <DealRow label={t("Renovation (from expenses)")} value={`+ ${fmt(renoSpent)}`} />
          <div style={S.dealSumLine} />
          <DealRow label={t("= All-in (project cost)")} value={fmt(allIn)} big />
          {hasPrice && <DealRow label={t("Cash invested (your expenses)")} value={fmt(total)} muted />}
          {holding.daysHeld != null && <DealRow label={t("Days held")} value={`${holding.daysHeld} ${t("days")}`} />}
          {holding.monthly > 0 && <DealRow label={t("Holding so far (est.)")} value={`${fmt(holding.total)} · ${fmt(holding.monthly)}/mo`} color="#b45309" />}
          {hasArv && <div style={S.dealDivide} />}
          {hasArv && isRent && (
            <DealRow label={t("Equity created")} value={fmt(equity)} pct={equityMargin.toFixed(0)} pctLabel={t("of ARV")} color={equity >= 0 ? "#16a34a" : "#dc2626"} />
          )}
          {isRent && cashFlow != null && (
            <DealRow label={t("Cash flow (est.)")} value={`${fmt(cashFlow)}/mo`} color={cashFlow >= 0 ? "#16a34a" : "#dc2626"} />
          )}
          {hasArv && !isRent && (
            <DealRow
              label={t("Projected profit")}
              value={fmt(projProfit)}
              pct={projMargin.toFixed(0)} pctLabel={t("margin")}
              color={projProfit >= 0 ? "#16a34a" : "#dc2626"}
            />
          )}
        </div>

        {hasBudget && (
          <>
            <div style={S.sectionLabel}>{t("Renovation budget")}</div>
            <div style={S.dealCard}>
              <div style={S.catTop}>
                <span style={S.catName}>{t("Reno spent")} {fmt(renoSpent)}</span>
                <span style={S.catAmt}>{t("of")} {fmt(house.budget)}</span>
              </div>
              <div style={S.barTrack}><div style={{ ...S.barFill, width: `${budgetPct}%`, background: renoSpent > house.budget ? "#dc2626" : ORANGE }} /></div>
              <div style={{ ...S.splitTotal, marginTop: 6, color: budgetDiff < 0 ? "#dc2626" : "#16a34a" }}>
                {budgetDiff < 0 ? `${t("Over budget by")} ${fmt(-budgetDiff)}` : `${fmt(budgetDiff)} ${t("left")}`}
              </div>
            </div>
          </>
        )}

        {sold && (
          <>
            <div style={S.sectionLabel}>{t("Sale result")}</div>
            <div style={S.dealCard}>
              <DealRow label={t("Sale price")} value={fmt(house.salePrice)} />
              <DealRow label={t("All-in (project cost)")} value={`− ${fmt(allIn)}`} />
              <DealRow label={t("Selling costs")} value={`− ${fmt(sellCosts)}`} />
              {holding.total > 0 && <DealRow label={t("Holding costs")} value={`− ${fmt(holding.total)}`} />}
              <div style={S.dealDivide} />
              <DealRow label={t("Net profit")} value={fmt(netProfit)} pct={roi.toFixed(0)} pctLabel={t("ROI")} color={netProfit >= 0 ? "#16a34a" : "#dc2626"} />
              <div style={S.dealDivide} />
              {members.map((p) => (
                <DealRow key={p.personId} label={`${p.name} (${house.splits[p.personId] || 0}%)`} value={fmt(profitShare[p.personId] || 0)} color={p.color} />
              ))}
            </div>
          </>
        )}

        <div style={S.sectionRow}>
          <div style={S.sectionLabel}>{t("Who paid what")}</div>
          {!locked && <button style={S.addMemberBtn} onClick={onAddMember}>{t("+ Add partner")}</button>}
        </div>
        <div style={S.partnerRow}>
          {nets.map((n) => (
            <div key={n.personId} style={{ ...S.partnerCard, borderColor: n.color }}>
              <div style={{ ...S.partnerName, color: n.color }}>{n.name}</div>
              <div style={S.partnerPaid}>{fmt(n.paid)}</div>
              <div style={S.partnerShould}>{t("of")} {fmt(n.should)} {t("owed")}</div>
              <div style={{ ...S.partnerNet, color: n.net >= 0 ? "#16a34a" : "#dc2626" }}>
                {n.net >= 0 ? "+" : "−"}{fmt(Math.abs(n.net))}
              </div>
            </div>
          ))}
        </div>

        <div style={S.sectionLabel}>{t("By category")}</div>
        <div style={S.catWrap}>
          {byCat.length === 0 && <Empty text={t("No expenses logged yet.")} />}
          {byCat.map((c) => {
            const pct = total ? (c.amt / total) * 100 : 0;
            return (
              <div key={c.id}>
                <div style={S.catTop}>
                  <span style={S.catName}>{findC(categories, c.id).emoji} {t(findC(categories, c.id).label)}</span>
                  <span style={S.catAmt}>{fmt(c.amt)}</span>
                </div>
                <div style={S.barTrack}><div style={{ ...S.barFill, width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>

        <div style={S.sectionLabel}>{t("Expenses by partner")}</div>
        {(() => {
          const groups = members
            .map((p) => ({ p, items: list.filter((e) => e.personId === p.personId) }))
            .filter((g) => g.items.length > 0);
          if (groups.length === 0) return <Empty text={t("No expenses yet. Start by logging the purchase price as your first expense.")} />;
          return groups.map(({ p, items }) => {
            const sub = items.reduce((s, e) => s + e.amount, 0);
            return (
              <div key={p.personId} style={{ marginBottom: 16 }}>
                <div style={{ ...S.expGroupHead, borderColor: p.color }}>
                  <span style={{ ...S.dot, background: p.color }} />
                  <span style={{ ...S.expGroupName, color: p.color }}>{p.name}</span>
                  <span style={S.expGroupTotalWrap}>
                    <span style={S.expGroupTotalLbl}>{t("Total paid")}</span>
                    <span style={S.expGroupSub}>{fmt(sub)}</span>
                  </span>
                </div>
                {items.map((e) => {
                  // Only the creator (or an admin) can edit/delete a record;
                  // anyone who can see the house can still view its receipts.
                  const editable = !locked && (isAdmin || (e.createdByPersonId || e.personId) === currentUserId);
                  const hasReceipts = (e.receipts || []).length > 0;
                  const clickable = editable || hasReceipts;
                  return (
                  <button key={e.id} style={{ ...S.expRow, ...(clickable ? {} : { cursor: "default" }) }}
                    onClick={() => (editable ? onEditExpense(e) : (hasReceipts ? setViewReceipts(e) : null))}>
                    <span style={{ ...S.expIcon, background: tintOf(p.color) }}>{findC(categories, e.category).emoji}</span>
                    <span style={S.expMid}>
                      <span style={S.expDesc}>{e.desc || t(findC(categories, e.category).label)}</span>
                      <span style={S.expMeta}>{t(findC(categories, e.category).label)} · {e.date}{e.source ? ` · ${e.source}` : ""}</span>
                    </span>
                    {hasReceipts && (
                      <span role="button" tabIndex={0} style={S.viewTag}
                        onClick={(ev) => { ev.stopPropagation(); setViewReceipts(e); }}>
                        {t("View")}{e.receipts.length > 1 ? ` ${e.receipts.length}` : ""}
                      </span>
                    )}
                    <span style={S.expAmt}>{fmt(e.amount)}</span>
                  </button>
                  );
                })}
              </div>
            );
          });
        })()}

        {/* ===== CLOSE OUT (accounting) — admin only ===== */}
        {isAdmin && (
        <div style={S.closeoutSection}>
          <div style={S.closeoutHead}>🔒 {t("Close out & settle")}</div>

          {!locked && (
            <div style={S.closeoutCard}>
              <div style={S.closeoutSub}>{t("Who owes who (preview)")}</div>
              {settlements.length === 0 ? (
                <div style={S.settled}>{t("✓ Even — no one owes anything.")}</div>
              ) : settlements.map((s, i) => (
                <div key={i} style={S.settleRow}>
                  <span style={{ ...S.dot, background: findP(partners, s.from).color }} />
                  <span style={S.settleText}>
                    <b style={{ color: findP(partners, s.from).color }}>{findP(partners, s.from).name}</b> {t("pays")}{" "}
                    <b style={{ color: findP(partners, s.to).color }}>{findP(partners, s.to).name}</b>
                  </span>
                  <span style={S.settleAmt}>{fmt(s.amount)}</span>
                </div>
              ))}
              {total > 0 && (
                <button style={S.closeBtn}
                  onClick={() => onCloseAccount(house.id, settlements.map((s) => ({ id: uid(), from: s.from, to: s.to, amount: s.amount, payments: [] })))}>
                  {t("Close account & start settling")}
                </button>
              )}
            </div>
          )}

          {closing && (
            <div style={S.closeoutCard}>
              <div style={S.closeNote}>{t("Books frozen on")} {house.settlement.createdAt}. {t("Record payments (partial allowed), then finalize.")}</div>
              {house.settlement.transfers.length === 0 ? (
                <div style={S.settled}>{t("✓ Even — no payment needed.")}</div>
              ) : house.settlement.transfers.map((tx) => {
                const paid = txPaid(tx); const rem = txRemaining(tx); const done = txSettled(tx);
                return (
                  <div key={tx.id} style={{ ...S.settleCard, ...(done ? { borderColor: "#16a34a", background: "#f1faf4" } : {}) }}>
                    <div style={S.settleTopRow}>
                      <span style={{ ...S.dot, background: findP(partners, tx.from).color }} />
                      <span style={S.settleText}>
                        <b style={{ color: findP(partners, tx.from).color }}>{findP(partners, tx.from).name}</b> {t("pays")}{" "}
                        <b style={{ color: findP(partners, tx.to).color }}>{findP(partners, tx.to).name}</b>
                        <span style={S.settleAmtInline}> · {fmt(tx.amount)}</span>
                      </span>
                    </div>
                    <div style={S.payProgress}>
                      <span style={{ color: "#16a34a", fontWeight: 800 }}>{t("Paid")} {fmt(paid)}</span>
                      <span style={{ color: done ? "#16a34a" : "#b45309", fontWeight: 800 }}>{done ? t("✓ Settled") : `${fmt(rem)} ${t("left")}`}</span>
                    </div>

                    {txPayments(tx).map((p) => (
                      <div key={p.id} style={S.payItem}>
                        <span style={S.paidStamp}>✓ {fmt(p.amount)}</span>
                        <span style={S.paidNoteText}>{p.date}{p.note ? ` · ${p.note}` : ""}</span>
                        <button style={{ ...S.undoLink, marginLeft: "auto" }} onClick={() => onRemovePayment(house.id, tx.id, p.id)}>{t("Remove")}</button>
                      </div>
                    ))}

                    {payingId === tx.id ? (
                      <div style={S.payPanel}>
                        <div style={S.fieldLabel}>{t("Amount paid")}</div>
                        <input style={S.input} type="number" min="0" step="0.01" inputMode="decimal" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                        <div style={{ ...S.fieldLabel, marginTop: 10 }}>{t("Date")}</div>
                        <DatePicker value={payDate} onChange={setPayDate} placeholder={t("Select date")} />
                        <div style={{ ...S.fieldLabel, marginTop: 10 }}>{t("Notes (where / how paid)")}</div>
                        <textarea style={{ ...S.input, height: 60, resize: "none" }} value={payNote} placeholder="e.g. Zelle, cash, Chase transfer…" onChange={(e) => setPayNote(e.target.value)} />
                        <div style={S.payPanelBtns}>
                          <button style={S.payCancel} onClick={() => setPayingId(null)}>{t("Cancel")}</button>
                          <button style={{ ...S.payConfirm, opacity: Number(payAmount) > 0 ? 1 : 0.5 }} onClick={() => confirmPay(tx.id)} disabled={!(Number(payAmount) > 0)}>{t("Record payment")}</button>
                        </div>
                      </div>
                    ) : (
                      !done && <button style={{ ...S.payToggle, marginTop: 8 }} onClick={() => startPay(tx)}>{t("+ Record payment")}</button>
                    )}
                  </div>
                );
              })}

              {house.settlement.transfers.every((tx) => txSettled(tx)) ? (
                <button style={S.finalizeBtn} onClick={() => onFinalize(house.id)}>{t("Finalize — close account")}</button>
              ) : (
                <div style={S.finalizeHint}>{t("Record the remaining balances to finalize.")}</div>
              )}
              <button style={S.reopenBtn} onClick={() => onReopen(house.id)}>{t("Reopen to edit expenses")}</button>
            </div>
          )}

          {closed && (
            <div style={S.closeoutCard}>
              <div style={S.closedBanner}>🔒 {t("Account closed on")} {house.closedAt}</div>
              {house.settlement.transfers.map((tx) => (
                <div key={tx.id} style={{ ...S.settleCard, borderColor: "#16a34a", background: "#f1faf4" }}>
                  <div style={S.settleTopRow}>
                    <span style={{ ...S.dot, background: findP(partners, tx.from).color }} />
                    <span style={S.settleText}>
                      <b style={{ color: findP(partners, tx.from).color }}>{findP(partners, tx.from).name}</b> {t("paid")}{" "}
                      <b style={{ color: findP(partners, tx.to).color }}>{findP(partners, tx.to).name}</b>
                      <span style={S.settleAmtInline}> · {fmt(tx.amount)}</span>
                    </span>
                  </div>
                  {txPayments(tx).map((p) => (
                    <div key={p.id} style={S.payItem}>
                      <span style={S.paidStamp}>✓ {fmt(p.amount)}</span>
                      <span style={S.paidNoteText}>{p.date}{p.note ? ` · ${p.note}` : ""}</span>
                    </div>
                  ))}
                </div>
              ))}
              <button style={S.reopenBtn} onClick={() => onReopen(house.id)}>{t("Reopen account")}</button>
            </div>
          )}
        </div>
        )}
      </div>

      {!locked && <div style={S.fabWrap}><button style={S.fab} onClick={onAddExpense}>{t("+ Add Expense")}</button></div>}

      {viewReceipts && (
        <ReceiptViewer expense={viewReceipts} categories={categories} onClose={() => setViewReceipts(null)} />
      )}

      {reportOpen && (
        <div style={S.overlay} onClick={closePdf}>
          <div style={S.pdfSheet} onClick={(e) => e.stopPropagation()}>
            <div style={S.sheetHead}>
              <span style={S.sheetTitle}>{house.name} — {t("Report")}</span>
              <button style={S.x} onClick={closePdf}>✕</button>
            </div>
            <div style={S.pdfScroll}>
              <ReportView house={house} expenses={expenses} partners={members} categories={categories} />
            </div>
            <div style={S.pdfBtns}>
              {pdfUrl ? (
                <>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={S.pdfOpen}>{t("Open / Print")}</a>
                  <a href={pdfUrl} download={pdfName} style={S.pdfDownload}>⤓ {t("Download PDF")}</a>
                </>
              ) : (
                <div style={{ ...S.pdfDownload, opacity: 0.6, cursor: "default" }}>{pdfBusy ? t("Preparing PDF…") : t("PDF unavailable")}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HouseForm({ data, partners, onSave, onDelete, onClose, isWide, selfId }) {
  const { t } = useT();
  const edit = !!data?.id;
  // A new house starts with just yourself selected — pick co-owners from
  // the existing partner list.
  const defaultMembers = data?.members || (selfId ? [selfId] : []);
  const [name, setName] = useState(data?.name || "");
  const [location, setLocation] = useState(data?.location || "");
  const [acquired, setAcquired] = useState(data?.acquired || today());
  const [status, setStatus] = useState(data?.status || "active");
  const [dealType, setDealType] = useState(data?.dealType || "flip");
  const [monthlyRent, setMonthlyRent] = useState(data?.monthlyRent != null ? String(data.monthlyRent) : "");
  const [purchasePrice, setPurchasePrice] = useState(data?.purchasePrice != null ? String(data.purchasePrice) : "");
  const [arv, setArv] = useState(data?.arv != null ? String(data.arv) : "");
  const [budget, setBudget] = useState(data?.budget != null ? String(data.budget) : "");
  const [holdingMonthly, setHoldingMonthly] = useState(data?.holdingMonthly != null ? String(data.holdingMonthly) : "");
  const [salePrice, setSalePrice] = useState(data?.salePrice != null ? String(data.salePrice) : "");
  const [sellingCosts, setSellingCosts] = useState(data?.sellingCosts != null ? String(data.sellingCosts) : "");
  const [notes, setNotes] = useState(data?.notes || "");
  const [selected, setSelected] = useState(defaultMembers);
  const [splits, setSplits] = useState(() => {
    if (data?.splits) { const s = {}; defaultMembers.forEach((id) => (s[id] = data.splits[id] || 0)); return s; }
    return evenSplitIds(defaultMembers);
  });
  const [confirmDel, setConfirmDel] = useState(false);
  const pool = partners;

  const selMembers = selected.map((id) => pool.find((p) => p.personId === id)).filter(Boolean);
  const splitSum = selected.reduce((s, id) => s + (Number(splits[id]) || 0), 0);
  const valid = name.trim().length > 0 && selected.length > 0 && splitSum === 100;
  const setPct = (id, v) => { const n = clamp(Math.round(Number(v) || 0), 0, 100); setSplits((s) => ({ ...s, [id]: n })); };
  const evenSplitNow = () => setSplits(evenSplitIds(selected));
  const numOrNull = (v) => (v === "" || isNaN(Number(v)) ? null : Number(v));

  const toggleMember = (id) => {
    setSelected((sel) => {
      const next = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
      setSplits(evenSplitIds(next));
      return next;
    });
  };

  const submit = () => {
    if (!valid) return;
    const cleanSplits = {}; selected.forEach((id) => (cleanSplits[id] = splits[id] || 0));
    onSave({ id: data?.id, name: name.trim(), location: location.trim(), acquired, status, dealType, notes: notes.trim(),
      members: selected, splits: cleanSplits,
      purchasePrice: numOrNull(purchasePrice),
      arv: numOrNull(arv), budget: numOrNull(budget), holdingMonthly: numOrNull(holdingMonthly), monthlyRent: numOrNull(monthlyRent), salePrice: numOrNull(salePrice), sellingCosts: numOrNull(sellingCosts) });
  };

  const span = { gridColumn: isWide ? "1 / -1" : "auto" };
  return (
    <div style={{ padding: isWide ? "26px 28px 8px" : "18px 16px 8px", display: isWide ? "grid" : "block", gridTemplateColumns: isWide ? "1fr 1fr" : undefined, columnGap: 24, alignItems: "start" }}>
      <Field label={t("House name")}><input style={S.input} value={name} placeholder="e.g. Sugar Land House" onChange={(e) => setName(e.target.value)} /></Field>
      <Field label={t("Location")}><input style={S.input} value={location} placeholder="City, TX" onChange={(e) => setLocation(e.target.value)} /></Field>
      <Field label={t("Acquired")}><DatePicker value={acquired} onChange={setAcquired} placeholder={t("Select date")} /></Field>
      <Field label={t("Status")}>
        <select style={S.input} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">{t("Active")}</option><option value="completed">{t("Completed")}</option>
        </select>
      </Field>

      <div style={span}>
      <Field label={t("Partners in this house")}>
        <div style={S.chipWrap}>
          {pool.map((p) => {
            const on = selected.includes(p.personId);
            return (
              <button key={p.personId} onClick={() => toggleMember(p.personId)}
                style={{ ...S.chip, ...(on ? { background: p.color, color: "#fff", borderColor: p.color } : {}) }}>
                {on ? "✓ " : ""}{p.name}
              </button>
            );
          })}
        </div>
        <div style={S.noteBox}>{t("To add a new partner, create their account in")} <b>Manage Employee</b> {t("and grant")} <b>Real Estate</b> {t("access — they'll then appear in this list.")}</div>
      </Field>

      <Field label={t("Ownership split")}>
        <div style={S.splitBox}>
          {selMembers.length === 0 && <div style={S.splitTotal}>{t("Select at least one partner above.")}</div>}
          {selMembers.map((p) => (
            <div key={p.personId} style={S.pctRow}>
              <span style={{ ...S.dot, background: p.color }} />
              <span style={S.slideName}>{p.name}</span>
              <input style={S.pctInput} type="number" min="0" max="100" inputMode="numeric"
                value={splits[p.personId] != null ? splits[p.personId] : 0}
                onChange={(e) => setPct(p.personId, e.target.value)} />
              <span style={S.pctSign}>%</span>
            </div>
          ))}
          {selMembers.length > 0 && (
            <div style={S.splitFooter}>
              <span style={{ fontWeight: 800, color: splitSum === 100 ? "#16a34a" : "#dc2626" }}>
                {t("Total")} {splitSum}%{splitSum === 100 ? " ✓" : splitSum < 100 ? ` · ${100 - splitSum}% ${t("left")}` : ` · ${splitSum - 100}% ${t("over")}`}
              </span>
              <button style={S.evenBtn} onClick={evenSplitNow}>{t("Even split")}</button>
            </div>
          )}
        </div>
      </Field>

      <Field label={t("Deal type")}>
        <div style={S.dealTypeRow}>
          <button style={{ ...S.dealTypeBtn, ...(dealType === "flip" ? S.dealTypeOn : {}) }} onClick={() => setDealType("flip")}>{t("🔁 Flip (sell)")}</button>
          <button style={{ ...S.dealTypeBtn, ...(dealType === "rent" ? S.dealTypeOn : {}) }} onClick={() => setDealType("rent")}>{t("🔑 Keep & rent")}</button>
        </div>
      </Field>

      <Field label={t("Deal economics")}>
        <div style={S.splitBox}>
          <Field label={t("Purchase price (full)")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder={t("e.g. 295000 — full property price")} value={purchasePrice} onChange={(e) => setPurchasePrice(e.target.value)} /></Field>
          <Row>
            <Field label={t("ARV (after-repair value)")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder="0" value={arv} onChange={(e) => setArv(e.target.value)} /></Field>
            <Field label={t("Renovation budget")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder="0" value={budget} onChange={(e) => setBudget(e.target.value)} /></Field>
          </Row>
          <Field label={t("Monthly holding cost")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder={t("interest + taxes + insurance + utilities + HOA")} value={holdingMonthly} onChange={(e) => setHoldingMonthly(e.target.value)} /></Field>
          {dealType === "rent" && (
            <Field label={t("Monthly rent (expected)")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder="0" value={monthlyRent} onChange={(e) => setMonthlyRent(e.target.value)} /></Field>
          )}
          <div style={S.splitTotal}>
            {dealType === "rent"
              ? t("Equity created = ARV − all-in (full purchase price + reno). Cash flow ≈ rent − monthly holding. Down payments are still logged as 'purchase' expenses.")
              : t("Profit = ARV − all-in (full purchase price + reno) − holding. Down payments are still logged as 'purchase' expenses. Leave purchase price blank to fall back to those expenses.")}
          </div>
        </div>
      </Field>

      {dealType === "flip" && status === "completed" && (
        <Field label={t("Sale (when sold)")}>
          <div style={S.splitBox}>
            <Row>
              <Field label={t("Sale price")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder="0" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} /></Field>
              <Field label={t("Selling costs")}><input style={S.input} type="number" min="0" inputMode="decimal" placeholder={t("commission + closing")} value={sellingCosts} onChange={(e) => setSellingCosts(e.target.value)} /></Field>
            </Row>
            <div style={S.splitTotal}>
              {t("Selling costs (agent + closing) usually run ≈ 8% of sale price")}{salePrice ? ` — ${t("about")} ${fmt(Math.round(Number(salePrice) * 0.08))}` : ""}.
            </div>
          </div>
        </Field>
      )}

      <Field label={t("Notes")}><textarea style={{ ...S.input, height: 64, resize: "none" }} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>

      <div style={S.formFooter}>
        <button style={{ ...S.primary, marginTop: 0, opacity: valid ? 1 : 0.5 }} onClick={submit} disabled={!valid}>{edit ? t("Save changes") : t("Create house")}</button>
        {edit && (
          <button style={{ ...S.danger, ...(confirmDel ? S.dangerArmed : {}) }} onClick={() => (confirmDel ? onDelete(data.id) : setConfirmDel(true))}>
            {confirmDel ? t("Tap again to delete house") : t("Delete house")}
          </button>
        )}
      </div>
      </div>
    </div>
  );
}

function ExpenseForm({ data, partners, categories, addCategory, currentUserId, onSave, onDelete, onClose }) {
  const { t } = useT();
  const edit = !!data?.id;
  const payer = findP(partners, edit ? data.personId : currentUserId);
  const [category, setCategory] = useState(data?.category || categories[0]?.id || "other");
  const [amount, setAmount] = useState(data?.amount != null ? String(data.amount) : "");
  const [date, setDate] = useState(data?.date || today());
  const [desc, setDesc] = useState(data?.desc || "");
  const [source, setSource] = useState(data?.source || "");
  const [receipts, setReceipts] = useState(data?.receipts || (data?.receipt ? [data.receipt] : []));
  const [addingCat, setAddingCat] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatEmoji, setNewCatEmoji] = useState("");
  const valid = Number(amount) > 0;

  const confirmAddCat = () => {
    if (!newCatName.trim()) return;
    const id = addCategory(newCatName, newCatEmoji.trim());
    setCategory(id);
    setNewCatName(""); setNewCatEmoji(""); setAddingCat(false);
  };

  const onFile = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setReceipts((rs) => [...rs, { id: uid(), name: f.name, type: f.type, size: f.size, dataUrl: reader.result }]);
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = () => {
    if (!valid) return;
    onSave({ id: data?.id, houseId: data.houseId, personId: payer.personId,
      category, amount: Number(amount), date, desc: desc.trim(), source: source.trim(), receipts });
  };

  return (
    <Modal title={edit ? t("Edit Expense") : t("Add Expense")} onClose={onClose} footer={
      <>
        <button style={{ ...S.primary, marginTop: 0, opacity: valid ? 1 : 0.5 }} onClick={submit} disabled={!valid}>{edit ? t("Save changes") : t("Add expense")}</button>
        {edit && (
          <button style={{ ...S.danger, ...(confirmDel ? S.dangerArmed : {}) }} onClick={() => (confirmDel ? onDelete(data.id) : setConfirmDel(true))}>
            {confirmDel ? t("Tap again to delete expense") : t("Delete expense")}
          </button>
        )}
      </>
    }>
      <Field label={t("Paid by")}>
        <div style={{ ...S.payChip, borderColor: payer.color, background: tintOf(payer.color) }}>
          <span style={{ ...S.dot, background: payer.color }} />
          <span style={{ fontWeight: 800, color: payer.color }}>{payer.name}</span>
          <span style={S.payNote}>{edit ? t("original payer") : t("your session")}</span>
        </div>
      </Field>
      <Row>
        <Field label={t("Amount")}><input style={S.input} type="number" min="0" step="0.01" inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label={t("Date")}><DatePicker value={date} onChange={setDate} placeholder={t("Select date")} /></Field>
      </Row>
      {date > today() && <div style={S.warnNote}>⚠ {t("Date is in the future — double-check it.")}</div>}
      {amount !== "" && Number(amount) <= 0 && <div style={S.warnNote}>⚠ {t("Amount must be greater than 0.")}</div>}
      <Field label={t("Category")}>
        <div style={S.catPickRow}>
          <select style={{ ...S.input, flex: 1 }} value={category} onChange={(e) => setCategory(e.target.value)}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.emoji} {t(c.label)}</option>)}
          </select>
          <button style={S.addCatBtn} onClick={() => setAddingCat((v) => !v)}>{addingCat ? "✕" : t("+ New")}</button>
        </div>
        {addingCat && (
          <div style={S.addCatPanel}>
            <div style={S.addCatInputs}>
              <input style={{ ...S.input, width: 64, textAlign: "center" }} value={newCatEmoji} placeholder="🏷️" maxLength={2} onChange={(e) => setNewCatEmoji(e.target.value)} />
              <input style={{ ...S.input, flex: 1 }} value={newCatName} placeholder={t("Category name")} onChange={(e) => setNewCatName(e.target.value)} />
            </div>
            <button style={{ ...S.primary, marginTop: 8, opacity: newCatName.trim() ? 1 : 0.5 }} onClick={confirmAddCat} disabled={!newCatName.trim()}>{t("Add category")}</button>
          </div>
        )}
      </Field>
      <Field label={t("Description")}><input style={S.input} value={desc} placeholder={t("What was it for?")} onChange={(e) => setDesc(e.target.value)} /></Field>
      <Field label={t("Source (note)")}><input style={S.input} value={source} placeholder="e.g. Home Depot invoice" onChange={(e) => setSource(e.target.value)} /></Field>
      <Field label={t("Receipts — photos or files")}>
        {receipts.map((r, i) => (
          <div key={r.id || i} style={{ ...S.receiptBox, marginBottom: 8 }}>
            {r.type && r.type.startsWith("image/")
              ? <img src={r.dataUrl} alt="receipt" style={S.receiptImg} />
              : <span style={S.receiptFileIcon}>📄</span>}
            <span style={S.receiptName}>{r.name}</span>
            <button style={S.undoLink} onClick={() => downloadDataUrl(r.name, r.dataUrl)}>{t("Download")}</button>
            <button style={S.receiptDel} onClick={() => setReceipts((rs) => rs.filter((x, idx) => (x.id ? x.id !== r.id : idx !== i)))}>{t("Remove")}</button>
          </div>
        ))}
        <label style={S.uploadBtn}>
          📎 {receipts.length ? t("Add another") : t("Add photo or file")}
          <input type="file" accept="image/*,application/pdf" multiple onChange={onFile} style={{ display: "none" }} />
        </label>
      </Field>
    </Modal>
  );
}

/* Read-only receipt viewer — lets any partner who can see the house view
   or download receipts on expenses they didn't create. "Open" previews
   the file inline (lightbox), not in a new tab. */
function ReceiptViewer({ expense, categories, onClose }) {
  const { t } = useT();
  const receipts = expense.receipts || [];
  const cat = findC(categories, expense.category);
  const [zoom, setZoom] = useState(null);
  const isImg = (r) => r && r.type && r.type.startsWith("image/");
  return (
    <Modal title={t("Receipts")} onClose={onClose}>
      <div style={{ ...S.houseLoc, marginTop: 0, marginBottom: 12 }}>{cat.emoji} {t(cat.label)} · {fmt(expense.amount)}{expense.date ? ` · ${expense.date}` : ""}</div>
      {receipts.length === 0 && <div style={S.splitTotal}>{t("No receipts attached.")}</div>}
      {receipts.map((r, i) => (
        <div key={r.id || i} style={{ ...S.receiptBox, marginBottom: 8 }}>
          {isImg(r)
            ? <img src={r.dataUrl} alt="receipt" style={{ ...S.receiptImg, cursor: "zoom-in" }} onClick={() => setZoom(r)} />
            : <span style={S.receiptFileIcon}>📄</span>}
          <span style={S.receiptName}>{r.name}</span>
          <button style={S.undoLink} onClick={() => setZoom(r)}>{t("Open")}</button>
          <button style={S.undoLink} onClick={() => downloadDataUrl(r.name, r.dataUrl)}>{t("Download")}</button>
        </div>
      ))}

      {zoom && (
        <div style={S.lightbox} onClick={() => setZoom(null)}>
          <div style={S.lightboxInner} onClick={(e) => e.stopPropagation()}>
            <div style={S.lightboxBar}>
              <span style={S.receiptName}>{zoom.name}</span>
              <button style={S.x} onClick={() => setZoom(null)}>✕</button>
            </div>
            {isImg(zoom)
              ? <img src={zoom.dataUrl} alt={zoom.name} style={S.lightboxImg} />
              : <iframe title={zoom.name} src={zoom.dataUrl} style={S.lightboxFrame} />}
            <div style={{ padding: 12, textAlign: "center", borderTop: `1px solid ${HAIR}` }}>
              <button style={S.payToggle} onClick={() => downloadDataUrl(zoom.name, zoom.dataUrl)}>⤓ {t("Download")}</button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function AddMemberForm({ house, partners, onAddExisting, onClose }) {
  const { t } = useT();
  const members = house.members || partners.map((p) => p.personId);
  const available = partners.filter((p) => !members.includes(p.personId));
  return (
    <Modal title={`${t("Add partner")} · ${house.name}`} onClose={onClose} footer={
      <button style={{ ...S.primary, marginTop: 0 }} onClick={onClose}>{t("Done")}</button>
    }>
      <div style={S.fieldLabel}>{t("In this house")}</div>
      <div style={S.chipWrap}>
        {members.map((id) => { const p = findP(partners, id); return <span key={id} style={{ ...S.chip, background: p.color, color: "#fff", borderColor: p.color }}>{p.name}</span>; })}
      </div>

      <div style={{ ...S.fieldLabel, marginTop: 16 }}>{t("Add partner")}</div>
      {available.length === 0
        ? <div style={S.noteBox}>{t("Everyone with Real Estate access is already on this house. To add someone new, create their account in")} <b>Manage Employee</b> {t("and grant")} <b>Real Estate</b> {t("access.")}</div>
        : available.map((p) => (
            <div key={p.personId} style={S.pRow}>
              <span style={{ ...S.dot, background: p.color }} />
              <span style={S.pName}>{p.name}</span>
              <button style={{ ...S.payToggle, marginLeft: "auto" }} onClick={() => onAddExisting(p.personId)}>{t("Add")}</button>
            </div>
          ))}
      <div style={{ ...S.splitTotal, marginTop: 12 }}>{t("Adding a partner resets the split to an even share — adjust it in Edit.")}</div>
    </Modal>
  );
}

function PartnersForm({ partners, expenses, addableUsers = [], onAddUser, onRemove, onClose }) {
  const { t } = useT();
  const [confirmId, setConfirmId] = useState(null);
  const [pickUser, setPickUser] = useState("");
  const addUser = () => { if (pickUser) { onAddUser(pickUser); setPickUser(""); } };
  return (
    <Modal title={t("Partners")} onClose={onClose}>
      <div style={S.noteBox}>{t("Partners are the company users with")} <b>{t("Real Estate")}</b> {t("access. Pick a user below to grant access instantly. Removing a partner revokes their Real Estate access.")}</div>

      <div style={{ ...S.fieldLabel, marginTop: 14 }}>{t("Add partner")}</div>
      {addableUsers.length === 0 ? (
        <div style={S.splitTotal}>{t("All company users already have Real Estate access. Create new accounts in Manage Employee.")}</div>
      ) : (
        <div style={S.catPickRow}>
          <select style={{ ...S.input, flex: 1 }} value={pickUser} onChange={(e) => setPickUser(e.target.value)}>
            <option value="">{t("Select a user…")}</option>
            {addableUsers.map((u) => <option key={u.userId} value={u.userId}>{u.name}{u.username ? ` (${u.username})` : ""}</option>)}
          </select>
          <button style={{ ...S.addCatBtn, opacity: pickUser ? 1 : 0.5 }} onClick={addUser} disabled={!pickUser}>{t("Add")}</button>
        </div>
      )}

      <div style={{ ...S.fieldLabel, marginTop: 18 }}>{t("Current partners")}</div>
      {partners.map((p) => {
        const locked = expenses.some((e) => e.personId === p.personId);
        const isAdminP = p.role === "admin";
        return (
          <div key={p.personId} style={S.pRow}>
            <span style={{ ...S.dot, background: p.color }} />
            <span style={{ ...S.pName, flex: 1, minWidth: 0 }}>{p.name}<span style={S.switchRole}> · {isAdminP ? t("Admin") : t("Partner")}</span></span>
            {isAdminP
              ? <span style={S.pLock}>{t("admin")}</span>
              : locked
                ? <span style={S.pLock}>{t("has expenses")}</span>
                : <button style={{ ...S.pRemove, ...(confirmId === p.personId ? { color: "#dc2626", fontWeight: 800 } : {}) }}
                    onClick={() => (confirmId === p.personId ? onRemove(p.personId) : setConfirmId(p.personId))}>
                    {confirmId === p.personId ? t("Revoke access?") : t("Remove")}
                  </button>}
          </div>
        );
      })}
    </Modal>
  );
}

function Modal({ title, onClose, children, footer }) {
  const { isWide } = useViewport();
  // Bottom sheet on mobile; centered dialog card on desktop.
  const overlayStyle = { ...S.overlay, alignItems: isWide ? "center" : "flex-end", padding: isWide ? 24 : 0 };
  const sheetStyle = isWide
    ? { ...S.sheet, maxWidth: 560, borderRadius: 22, maxHeight: "88vh" }
    : S.sheet;
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div style={S.sheetHead}><span style={S.sheetTitle}>{title}</span><button style={S.x} onClick={onClose}>✕</button></div>
        <div style={S.sheetBody}>{children}</div>
        {footer && <div style={S.sheetFooter}>{footer}</div>}
      </div>
    </div>
  );
}
const Field = ({ label, children }) => (<div style={{ marginBottom: 12 }}><div style={S.fieldLabel}>{label}</div>{children}</div>);
const Row = ({ children }) => (
  <div style={{ display: "flex", gap: 10 }}>
    {React.Children.map(children, (ch) => <div style={{ flex: 1, minWidth: 0 }}>{ch}</div>)}
  </div>
);
const Mini = ({ label, value }) => (<div style={S.mini}><div style={S.miniVal}>{value}</div><div style={S.miniLbl}>{label}</div></div>);
const Empty = ({ text }) => <div style={S.empty}>{text}</div>;
const DealRow = ({ label, value, big, color, pct, pctLabel, muted }) => (
  <div style={S.dealRow}>
    <span style={{ ...S.dealLabel, ...(muted ? { color: "#98A49B" } : {}) }}>{label}</span>
    {pct != null ? (
      <span style={S.dealValWrap}>
        <span style={{ ...S.dealValBig, color }}>{value}</span>
        <span style={{ ...S.dealPct, color }}>{pct}% {pctLabel}</span>
      </span>
    ) : (
      <span style={{ ...(big ? S.dealValBig : S.dealVal), ...(color ? { color } : {}), ...(muted ? { color: "#98A49B", fontWeight: 600 } : {}) }}>{value}</span>
    )}
  </div>
);

function ReportView({ house, expenses, partners, categories }) {
  const { total, renoSpent, purchaseBasis, allIn, hasPrice } = houseEconomics(house, expenses);
  const shouldPay = splitByPct(total, house.splits, partners);
  const statusLbl = house.closed ? "Closed" : house.settlement ? "Closing" : house.status === "completed" ? "Completed" : "Active";
  const sold = house.status === "completed" && house.salePrice != null;
  const hold = holdingInfo(house);
  const net = sold ? house.salePrice - allIn - (house.sellingCosts || 0) - hold.total : null;
  const sorted = [...expenses].sort((a, b) => (a.date < b.date ? -1 : 1));
  const imgs = [];
  expenses.forEach((e) => (e.receipts || []).forEach((r) => { if (r.type && r.type.startsWith("image/")) imgs.push({ e, r }); }));
  const R = RS;
  return (
    <div style={R.page}>
      <div style={R.title}>{house.name}</div>
      <div style={R.meta}>{house.location || "—"} · {statusLbl} · Generated {today()}</div>

      <div style={R.h}>Deal economics</div>
      {house.arv != null && <Line l="ARV (after-repair value)" v={fmt(house.arv)} />}
      <Line l={hasPrice ? "Purchase price (full)" : "Purchase (from expenses)"} v={fmt(purchaseBasis)} />
      <Line l="Renovation (from expenses)" v={`+ ${fmt(renoSpent)}`} />
      <Line l="All-in (project cost)" v={fmt(allIn)} bold />
      {hasPrice && <Line l="Cash invested (expenses)" v={fmt(total)} />}
      {hold.daysHeld != null && <Line l="Days held" v={`${hold.daysHeld} days`} />}
      {hold.monthly > 0 && <Line l="Holding (est.)" v={`${fmt(hold.total)} · ${fmt(hold.monthly)}/mo`} />}
      {house.budget ? <Line l="Renovation budget" v={`${fmt(renoSpent)} of ${fmt(house.budget)}`} /> : null}
      {house.arv != null && <Line l="Projected profit" v={`${fmt(house.arv - allIn - hold.total)} (${(((house.arv - allIn - hold.total) / house.arv) * 100).toFixed(0)}%)`} bold />}
      {sold && <Line l="Sale price" v={fmt(house.salePrice)} />}
      {sold && <Line l="Selling costs" v={fmt(house.sellingCosts || 0)} />}
      {sold && hold.total > 0 && <Line l="Holding costs" v={fmt(hold.total)} />}
      {sold && <Line l="Net profit" v={`${fmt(net)} (ROI ${allIn ? ((net / allIn) * 100).toFixed(0) : 0}%)`} bold />}

      <div style={R.h}>Who paid what</div>
      {partners.map((p) => {
        const paid = expenses.filter((e) => e.personId === p.personId).reduce((s, e) => s + e.amount, 0);
        const n = paid - (shouldPay[p.personId] || 0);
        return <Line key={p.personId} l={`${p.name} (${house.splits[p.personId] || 0}%)`} v={`paid ${fmt(paid)} · ${n >= 0 ? "+" : "−"}${fmt(Math.abs(n))}`} />;
      })}

      <div style={R.h}>{house.closed ? "Account closed" : "Who owes who"}</div>
      {house.settlement
        ? (<>
            {house.closedAt && <div style={R.meta}>Closed on {house.closedAt}</div>}
            {house.settlement.transfers.length === 0
              ? <div style={R.muted}>Even — no payment needed.</div>
              : house.settlement.transfers.map((t) => (
                  <Line key={t.id} l={`${findP(partners, t.from).name} → ${findP(partners, t.to).name}`}
                    v={`${fmt(t.amount)}${txSettled(t) ? " · paid in full" : ` · paid ${fmt(txPaid(t))}, ${fmt(txRemaining(t))} left`}`} />
                ))}
          </>)
        : <div style={R.muted}>Open — not reconciled yet.</div>}

      <div style={R.h}>Expenses by partner</div>
      {partners.map((p) => {
        const items = sorted.filter((e) => e.personId === p.personId);
        if (items.length === 0) return null;
        const sub = items.reduce((s, e) => s + e.amount, 0);
        return (
          <div key={p.personId} style={{ marginBottom: 12 }}>
            <div style={{ ...R.partnerHead, borderColor: p.color }}>
              <span style={{ color: p.color }}>{p.name}</span>
              <span>Total paid {fmt(sub)}</span>
            </div>
            {items.map((e) => (
              <div key={e.id} style={R.trow}>
                <span style={{ flex: 2 }}>{e.date}</span>
                <span style={{ flex: 2 }}>{findC(categories, e.category).emoji} {findC(categories, e.category).label}{e.receipts && e.receipts.length ? ` 📎${e.receipts.length}` : ""}<br /><span style={R.muted}>{e.desc || ""}</span></span>
                <span style={{ flex: 1, textAlign: "right", fontWeight: 700 }}>{fmt(e.amount)}</span>
              </div>
            ))}
          </div>
        );
      })}
      <div style={R.totalRow}><span>TOTAL (all partners)</span><span>{fmt(total)}</span></div>

      {imgs.length > 0 && (<>
        <div style={R.h}>Receipts</div>
        <div style={R.imgGrid}>
          {imgs.map(({ e, r }, i) => (
            <div key={i} style={R.imgCell}>
              <img src={r.dataUrl} alt="receipt" style={R.img} />
              <div style={R.imgCap}>{findC(categories, e.category).label} · {fmt(e.amount)}</div>
            </div>
          ))}
        </div>
      </>)}
    </div>
  );
}
const Line = ({ l, v, bold }) => (
  <div style={RS.line}><span style={RS.lineL}>{l}</span><span style={{ ...RS.lineV, fontWeight: bold ? 800 : 600 }}>{v}</span></div>
);

const GREEN = "linear-gradient(150deg,#15463a 0%,#1c6b50 52%,#249668 100%)";
const GREEN_SOLID = "#15463a";
const ORANGE = "linear-gradient(135deg,#e08a2b,#f2b34d)";
const PAPER = "#F4F2EC";
const INK = "#13231C";
const HAIR = "#ECE9E0";
const SHADOW = "0 1px 2px rgba(19,35,28,.05), 0 12px 30px -16px rgba(19,35,28,.20)";
const SHADOW_SM = "0 1px 2px rgba(19,35,28,.05), 0 6px 16px -10px rgba(19,35,28,.16)";
const RS = {
  page: { padding: "18px 16px 24px", color: "#16241c", fontFamily: "'DM Sans',system-ui,sans-serif" },
  title: { fontSize: 22, fontWeight: 800 },
  meta: { fontSize: 12, color: "#8a978f", marginTop: 2, marginBottom: 4 },
  h: { fontSize: 12, fontWeight: 800, color: "#0f5132", textTransform: "uppercase", letterSpacing: 0.6, margin: "16px 0 8px", borderBottom: "2px solid #e3e8e4", paddingBottom: 4 },
  line: { display: "flex", justifyContent: "space-between", gap: 12, padding: "4px 0", fontSize: 13.5 },
  lineL: { color: "#5b6b62" },
  lineV: { color: "#16241c", textAlign: "right" },
  muted: { color: "#8a978f", fontSize: 12.5 },
  thead: { display: "flex", gap: 8, fontSize: 11, fontWeight: 800, color: "#8a978f", textTransform: "uppercase", padding: "4px 0", borderBottom: "1px solid #e3e8e4" },
  partnerHead: { display: "flex", justifyContent: "space-between", fontSize: 14.5, fontWeight: 800, padding: "6px 0", borderBottom: "2px solid", marginBottom: 2 },
  trow: { display: "flex", gap: 8, fontSize: 12.5, padding: "8px 0", borderBottom: "1px solid #f0f2f0", lineHeight: 1.35 },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 800, padding: "10px 0", borderTop: "2px solid #16241c", marginTop: 4 },
  imgGrid: { display: "flex", flexWrap: "wrap", gap: 10 },
  imgCell: { width: "calc(50% - 5px)" },
  img: { width: "100%", borderRadius: 8, border: "1px solid #e3e8e4" },
  imgCap: { fontSize: 11, color: "#8a978f", marginTop: 3 },
};

const S = {
  app: { fontFamily: "'DM Sans',system-ui,sans-serif", background: "#E7E3D8", minHeight: "100vh" },
  toast: { position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", background: INK, color: "#fff", fontSize: 14.5, fontWeight: 700, padding: "13px 22px", borderRadius: 999, zIndex: 100, boxShadow: "0 12px 34px rgba(0,0,0,.34)", pointerEvents: "none", maxWidth: 400, textAlign: "center", letterSpacing: 0.2 },
  frame: { width: "100%", maxWidth: 430, background: PAPER, minHeight: "100vh", position: "relative", paddingBottom: 96, boxShadow: "0 0 60px rgba(19,35,28,.10)" },
  // Responsive multi-column card grid for the houses list & portfolio on
  // desktop; the parent renders this only when isWide.
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: 14, alignItems: "start" },
  // Sticky-feeling footer for the full-screen House form (save / delete).
  formFooter: { marginTop: 18, paddingTop: 16, borderTop: `1px solid ${HAIR}` },

  langBar: { display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "8px 14px", background: GREEN_SOLID, borderBottom: "1px solid rgba(228,206,146,.18)" },
  langBtn: { display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,.10)", color: "#fff", border: "1px solid rgba(228,206,146,.35)", borderRadius: 999, padding: "6px 12px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.3 },
  langBackdrop: { position: "fixed", inset: 0, zIndex: 9000 },
  langMenu: { position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 14, boxShadow: "0 16px 40px rgba(19,35,28,.22)", border: `1px solid ${HAIR}`, padding: 6, zIndex: 9001, minWidth: 168 },
  langItem: { display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", borderRadius: 10, padding: "10px 12px", fontSize: 14.5, color: INK, cursor: "pointer", fontFamily: "inherit" },

  session: { display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: GREEN_SOLID, color: "#D8E6DD" },
  sessionLbl: { fontSize: 11.5, fontWeight: 600, color: "rgba(216,230,221,.8)" },
  sessionPill: { display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,.10)", color: "#fff", border: "1px solid rgba(228,206,146,.35)", borderRadius: 999, padding: "5px 11px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  sessionPillName: { fontWeight: 800 },
  sessionCaret: { opacity: 0.6, fontSize: 10, marginLeft: 1 },
  sessionStatic: { display: "inline-flex", alignItems: "center", gap: 7, color: "#fff", fontSize: 13, fontWeight: 800 },
  previewChip: { fontSize: 9.5, fontWeight: 800, background: "#E4CE92", color: "#15463a", padding: "3px 8px", borderRadius: 999, letterSpacing: 0.6 },
  switchRole: { fontSize: 11.5, color: "#8a978f", fontWeight: 700 },
  noteBox: { fontSize: 12, color: "#7A5E1E", background: "#F6EFDD", border: "1px solid #E7D9B0", borderRadius: 10, padding: "10px 12px", marginTop: 10, lineHeight: 1.45 },
  viewTag: { flexShrink: 0, fontSize: 12, fontWeight: 700, color: GREEN_SOLID, background: "#EAF5EE", border: "1px solid #cfe6d8", borderRadius: 8, padding: "6px 12px", marginRight: 10, cursor: "pointer", letterSpacing: 0.2 },
  lightbox: { position: "fixed", inset: 0, background: "rgba(13,28,21,.82)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 },
  lightboxInner: { background: "#fff", borderRadius: 16, maxWidth: "min(900px, 96vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.4)" },
  lightboxBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 14px", borderBottom: `1px solid ${HAIR}` },
  lightboxImg: { maxWidth: "100%", maxHeight: "72vh", objectFit: "contain", display: "block", margin: "0 auto", background: "#111" },
  lightboxFrame: { width: "min(900px, 92vw)", height: "72vh", border: "none" },
  sessionBtn: { marginLeft: "auto", background: "rgba(255,255,255,.10)", color: "#E4CE92", border: "1px solid rgba(228,206,146,.35)", borderRadius: 9, padding: "6px 11px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

  switchHint: { fontSize: 12, color: "#8a978f", marginBottom: 12 },
  switchRow: { display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 12px", background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left", boxShadow: SHADOW_SM },
  switchRowOn: { background: "#EAF5EE", borderColor: "#1c6b50" },
  switchAvatar: { width: 34, height: 34, borderRadius: "50%", color: "#fff", fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  switchName: { flex: 1, fontSize: 14.5, fontWeight: 700, color: INK },
  switchActive: { fontSize: 9.5, fontWeight: 800, background: "#15463a", color: "#E4CE92", padding: "3px 8px", borderRadius: 999, letterSpacing: 0.6 },

  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "22px 18px 24px", background: GREEN, color: "#fff" },
  eyebrow: { fontSize: 10.5, letterSpacing: 2.2, textTransform: "uppercase", color: "#E4CE92", fontWeight: 700 },
  h1: { fontSize: 23, fontWeight: 800, marginTop: 3, letterSpacing: -0.3 },
  headerIcon: { fontSize: 30 },
  portfolioBtn: { display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.14)", border: "1px solid rgba(228,206,146,.5)", color: "#F3E8C8", borderRadius: 999, padding: "9px 15px", fontSize: 17, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", backdropFilter: "blur(4px)" },
  portfolioBtnLbl: { fontSize: 13, fontWeight: 700, letterSpacing: 0.2 },
  kpiGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 },
  kpiCard: { background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 18, padding: "16px 18px", boxShadow: SHADOW_SM },
  kpiLabel: { fontSize: 11, fontWeight: 700, color: "#7C8A80", textTransform: "uppercase", letterSpacing: 0.7 },
  kpiValue: { fontSize: 31, fontWeight: 800, color: INK, marginTop: 5, letterSpacing: -0.5, fontVariantNumeric: "tabular-nums" },
  kpiValueSm: { fontSize: 20, fontWeight: 800, color: INK, marginTop: 7, letterSpacing: -0.3, fontVariantNumeric: "tabular-nums" },
  portRow: { display: "block", width: "100%", textAlign: "left", background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 20, padding: 18, marginBottom: 13, cursor: "pointer", fontFamily: "inherit", boxShadow: SHADOW_SM },
  portRowTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  portName: { fontSize: 17.5, fontWeight: 800, color: INK, letterSpacing: -0.2 },
  portLoc: { fontSize: 13, color: "#7a8a80", marginTop: 4 },
  portStatsRow: { display: "flex", gap: 12, marginTop: 14 },
  portStat: { flex: 1, display: "flex", flexDirection: "column", gap: 3, fontSize: 16, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums" },
  portStatLbl: { fontSize: 10.5, fontWeight: 700, color: "#98A49B", textTransform: "uppercase", letterSpacing: 0.6 },
  portPartners: { marginTop: 14, paddingTop: 14, borderTop: `1px solid ${HAIR}` },
  portPartnersLbl: { fontSize: 10.5, fontWeight: 700, color: "#98A49B", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 },
  portPartnerRow: { display: "flex", alignItems: "center", gap: 8, padding: "4px 0" },
  portPartnerName: { fontSize: 14, fontWeight: 600, color: INK },
  portPartnerPct: { fontSize: 12, color: "#8a978f", fontWeight: 700 },
  portPartnerAmt: { marginLeft: "auto", fontSize: 14.5, fontWeight: 800 },
  partnerCard: { background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 18, padding: 16, marginBottom: 13, boxShadow: SHADOW_SM },
  partnerCardHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  partnerCardName: { fontSize: 15.5, fontWeight: 800, letterSpacing: -0.2 },
  dealTypeRow: { display: "flex", gap: 10 },
  dealTypeBtn: { flex: 1, padding: "13px 8px", borderRadius: 13, border: `1px solid ${HAIR}`, background: "#fff", color: "#6B7A70", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  dealTypeOn: { background: GREEN, color: "#fff", borderColor: "transparent", boxShadow: SHADOW_SM },
  ppWrap: { borderBottom: `1px solid ${HAIR}` },
  ppRow: { display: "flex", alignItems: "center", gap: 10, width: "100%", background: "none", border: "none", padding: "13px 0", cursor: "pointer", fontFamily: "inherit", textAlign: "left" },
  ppName: { fontSize: 15.5, fontWeight: 700, color: INK },
  ppTotal: { marginLeft: "auto", fontSize: 17, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: -0.2 },
  ppChevron: { fontSize: 12, color: "#98A49B", width: 14, textAlign: "center" },
  ppBreak: { padding: "0 0 10px 22px" },
  ppBreakRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0" },
  ppBreakName: { fontSize: 13.5, color: "#6B7A70", fontWeight: 600 },
  ppBreakAmt: { fontSize: 14, fontWeight: 800, fontVariantNumeric: "tabular-nums" },
  ppGrand: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12, paddingTop: 13, borderTop: `2px solid ${INK}` },
  ppGrandLbl: { fontSize: 13, fontWeight: 800, color: INK },
  ppGrandVal: { fontSize: 18, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: -0.3 },

  statStrip: { display: "flex", gap: 12, padding: "16px 16px 0", marginTop: 0 },
  mini: { flex: 1, background: GREEN, color: "#fff", borderRadius: 16, padding: "14px 16px", boxShadow: "0 14px 30px -14px rgba(21,70,58,.5)", border: "1px solid rgba(228,206,146,.18)" },
  miniVal: { fontSize: 19, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: -0.3 },
  miniLbl: { fontSize: 10.5, color: "#E4CE92", marginTop: 3, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700 },

  body: { padding: "18px 16px 8px" },
  tabBar: { display: "flex", gap: 8, padding: "16px 16px 0" },
  tab: { flex: 1, background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 13, padding: "11px", fontSize: 14, fontWeight: 800, color: "#6B7A70", cursor: "pointer", fontFamily: "inherit", boxShadow: SHADOW_SM },
  tabOn: { background: GREEN, color: "#fff", borderColor: "transparent", boxShadow: SHADOW },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: "#7C8A80", textTransform: "uppercase", letterSpacing: 1.4, margin: "22px 2px 10px" },
  sectionRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  addMemberBtn: { background: "#F6EFDD", border: "1px solid #E7D9B0", color: "#7A5E1E", borderRadius: 999, padding: "6px 13px", fontSize: 12.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", marginTop: 10 },
  chipWrap: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: { background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 999, padding: "10px 15px", fontSize: 14, fontWeight: 700, color: "#2a382f", cursor: "pointer", fontFamily: "inherit", boxShadow: SHADOW_SM },
  chipAdd: { background: "transparent", border: "1px dashed #C2C9BE", borderRadius: 999, padding: "10px 15px", fontSize: 14, fontWeight: 800, color: GREEN_SOLID, cursor: "pointer", fontFamily: "inherit" },

  houseCard: { width: "100%", textAlign: "left", background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 20, padding: 17, marginBottom: 13, cursor: "pointer", display: "block", boxShadow: SHADOW },
  houseHeadRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  houseName: { fontSize: 17.5, fontWeight: 800, color: INK, letterSpacing: -0.2 },
  houseLoc: { fontSize: 13, color: "#7C8A80", marginTop: 5 },
  houseFootRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 14 },
  cardProfitRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, paddingTop: 12, borderTop: `1px solid ${HAIR}` },
  cardProfitLbl: { fontSize: 12, color: "#7C8A80", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },
  cardProfitVal: { fontSize: 16.5, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: -0.2 },
  houseTotal: { fontSize: 21, fontWeight: 800, color: GREEN_SOLID, fontVariantNumeric: "tabular-nums", letterSpacing: -0.4 },
  houseMeta: { fontSize: 12, color: "#98A49B" },
  badge: { fontSize: 10, fontWeight: 800, padding: "4px 10px", borderRadius: 999, textTransform: "uppercase", letterSpacing: 0.7 },
  badgeActive: { background: "#E8F4EC", color: "#15643F" },
  badgeDone: { background: "#EEF0FF", color: "#4338ca" },
  badgeClosing: { background: "#FBF0DD", color: "#9A6B12" },
  badgeClosed: { background: INK, color: "#D8C79A" },

  detailHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 14px 0" },
  back: { display: "flex", alignItems: "center", gap: 4, background: "#fff", border: `1px solid ${HAIR}`, color: GREEN_SOLID, fontWeight: 800, fontSize: 15.5, cursor: "pointer", padding: "9px 16px 9px 12px", borderRadius: 999, fontFamily: "inherit", boxShadow: SHADOW_SM },
  editLink: { background: "none", border: "none", color: "#0891b2", fontWeight: 700, fontSize: 14, cursor: "pointer" },
  pdfLink: { background: "#fff", border: `1px solid ${HAIR}`, color: GREEN_SOLID, fontWeight: 800, fontSize: 13.5, cursor: "pointer", borderRadius: 999, padding: "7px 13px", fontFamily: "inherit", boxShadow: SHADOW_SM },
  detailTitleWrap: { padding: "10px 18px 0", color: INK },

  hero: { margin: "16px 16px 0", background: GREEN, color: "#fff", borderRadius: 22, padding: "22px 22px", boxShadow: "0 18px 40px -16px rgba(21,70,58,.55)", border: "1px solid rgba(228,206,146,.18)" },
  heroLabel: { fontSize: 11, color: "#E4CE92", textTransform: "uppercase", letterSpacing: 1.8, fontWeight: 700 },
  heroValue: { fontSize: 36, fontWeight: 800, marginTop: 6, letterSpacing: -1, fontVariantNumeric: "tabular-nums" },
  heroSub: { fontSize: 12.5, color: "rgba(255,255,255,.82)", marginTop: 8 },

  dealCard: { background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 18, padding: "16px 18px", marginBottom: 4, boxShadow: SHADOW_SM },
  dealRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" },
  dealLabel: { fontSize: 13.5, color: "#6B7A70" },
  dealVal: { fontSize: 14.5, fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums" },
  dealValBig: { fontSize: 20, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums", letterSpacing: -0.3 },
  dealValWrap: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 },
  dealPct: { fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 999, background: "#F1EFE7", textTransform: "uppercase", letterSpacing: 0.5 },
  dealDivide: { height: 1, background: HAIR, margin: "8px 0" },
  dealSumLine: { height: 2, background: "#16241c", margin: "8px 0 6px", borderRadius: 2 },

  partnerRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  partnerName: { fontSize: 14, fontWeight: 800 },
  partnerPaid: { fontSize: 19, fontWeight: 800, color: "#16241c", marginTop: 4 },
  partnerShould: { fontSize: 11, color: "#8a978f", marginTop: 2 },
  partnerNet: { fontSize: 13, fontWeight: 800, marginTop: 6 },

  settled: { background: "#e7f6ec", color: "#0f5132", fontWeight: 700, fontSize: 14, padding: "12px 14px", borderRadius: 12 },
  settleRow: { display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #e3e8e4", borderRadius: 12, padding: "11px 13px", marginBottom: 8 },
  settleCard: { background: "#fff", border: "1px solid #e3e8e4", borderRadius: 12, padding: "11px 13px", marginBottom: 8 },
  settleTopRow: { display: "flex", alignItems: "center", gap: 10 },
  paidInfo: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8, paddingLeft: 19 },
  paidNoteText: { fontSize: 12.5, color: "#5b6b62" },
  undoLink: { background: "none", border: "none", color: "#0891b2", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0 },
  payPanel: { marginTop: 10, paddingTop: 10, borderTop: "1px dashed #cfd8d2" },
  payPanelBtns: { display: "flex", gap: 8, marginTop: 10 },
  payCancel: { flex: 1, background: "#eef1ef", border: "none", borderRadius: 11, padding: "11px", fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#5b6b62", fontFamily: "inherit" },
  payConfirm: { flex: 2, background: "#16a34a", border: "none", borderRadius: 11, padding: "11px", fontSize: 14, fontWeight: 800, cursor: "pointer", color: "#fff", fontFamily: "inherit" },
  dot: { width: 9, height: 9, borderRadius: 999, flexShrink: 0, display: "inline-block" },
  settleText: { flex: 1, fontSize: 13.5, color: "#2a382f" },
  settleAmt: { fontSize: 15, fontWeight: 800, color: "#16241c" },
  settleAmtInline: { fontWeight: 800, color: "#16241c" },
  closeBtn: { width: "100%", background: INK, color: "#fff", border: "none", borderRadius: 14, padding: "14px", fontSize: 15, fontWeight: 800, cursor: "pointer", marginTop: 6, fontFamily: "inherit", letterSpacing: 0.2, boxShadow: SHADOW },
  closeoutSection: { marginTop: 26, marginBottom: 8 },
  closeoutHead: { background: GREEN, color: "#fff", fontSize: 13.5, fontWeight: 800, padding: "14px 16px", borderRadius: "18px 18px 0 0", letterSpacing: 0.6, textTransform: "uppercase" },
  closeoutCard: { background: "#fff", border: `1px solid ${HAIR}`, borderTop: "none", borderRadius: "0 0 18px 18px", padding: 16, boxShadow: SHADOW_SM },
  closeoutSub: { fontSize: 11.5, fontWeight: 700, color: "#8a978f", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 },
  payProgress: { display: "flex", justifyContent: "space-between", fontSize: 13, margin: "8px 0" },
  payItem: { display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid #f0f2f0" },
  finalizeHint: { textAlign: "center", fontSize: 12.5, color: "#b45309", fontWeight: 700, marginTop: 10 },
  closeNote: { fontSize: 12.5, color: "#8a978f", marginBottom: 10 },
  payToggle: { background: "#fff", border: "1px solid #d8e0da", borderRadius: 999, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer", color: "#5b6b62", fontFamily: "inherit", flexShrink: 0 },
  payToggleOn: { background: "#16a34a", border: "1px solid #16a34a", color: "#fff" },
  finalizeBtn: { width: "100%", background: GREEN, color: "#fff", border: "none", borderRadius: 14, padding: "15px", fontSize: 15.5, fontWeight: 800, cursor: "pointer", marginTop: 8, fontFamily: "inherit", letterSpacing: 0.2, boxShadow: SHADOW },
  reopenBtn: { width: "100%", background: "none", color: "#7C8A80", border: "none", padding: "12px", fontSize: 13.5, fontWeight: 700, cursor: "pointer", marginTop: 4, fontFamily: "inherit" },
  closedBanner: { background: "#23362b", color: "#cfe8da", fontWeight: 800, fontSize: 14, padding: "12px 14px", borderRadius: 12, marginBottom: 10, textAlign: "center" },
  paidStamp: { fontSize: 12.5, fontWeight: 800, color: "#16a34a", flexShrink: 0 },

  catWrap: { display: "flex", flexDirection: "column", gap: 12 },
  catTop: { display: "flex", justifyContent: "space-between", marginBottom: 5 },
  catName: { fontSize: 13.5, fontWeight: 600, color: "#2a382f" },
  catAmt: { fontSize: 13.5, fontWeight: 800, color: "#16241c" },
  barTrack: { height: 7, background: "#e3e8e4", borderRadius: 999, overflow: "hidden" },
  barFill: { height: "100%", background: ORANGE, borderRadius: 999 },

  filterBar: { display: "flex", gap: 8, marginBottom: 12 },
  select: { flex: 1, padding: "9px 10px", borderRadius: 10, border: "1px solid #d8e0da", background: "#fff", fontSize: 13, fontFamily: "inherit", color: "#2a382f" },

  expRow: { width: "100%", display: "flex", alignItems: "center", gap: 12, background: "#fff", border: "1px solid #e3e8e4", borderRadius: 14, padding: "11px 13px", marginBottom: 8, cursor: "pointer", textAlign: "left" },
  expGroupHead: { display: "flex", alignItems: "center", gap: 8, padding: "4px 4px 8px", borderBottom: "2px solid", marginBottom: 8 },
  expGroupName: { fontSize: 14, fontWeight: 800 },
  expGroupTotalWrap: { marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end" },
  expGroupTotalLbl: { fontSize: 10, fontWeight: 700, color: "#8a978f", textTransform: "uppercase", letterSpacing: 0.5 },
  expGroupSub: { fontSize: 15, fontWeight: 800, color: "#16241c" },
  expIcon: { width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 },
  expMid: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
  expDesc: { fontSize: 14, fontWeight: 700, color: "#16241c", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  expMeta: { fontSize: 11.5, color: "#8a978f", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  expAmt: { fontSize: 15, fontWeight: 800, color: "#0f5132", flexShrink: 0 },

  fabRound: { position: "fixed", bottom: 28, right: 24, width: 60, height: 60, borderRadius: "50%", background: GREEN_SOLID, color: "#fff", border: "1px solid rgba(228,206,146,.3)", cursor: "pointer", boxShadow: "0 14px 34px -8px rgba(21,70,58,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 40 },
  fabWrap: { position: "fixed", bottom: 0, left: 0, right: 0, display: "flex", justifyContent: "center", pointerEvents: "none", paddingBottom: 18 },
  fab: { pointerEvents: "auto", width: "min(398px,calc(100% - 32px))", background: GREEN, color: "#fff", border: "1px solid rgba(228,206,146,.22)", borderRadius: 16, padding: "16px", fontSize: 16, fontWeight: 800, cursor: "pointer", boxShadow: "0 16px 36px -12px rgba(21,70,58,.55)", fontFamily: "inherit", letterSpacing: 0.2 },

  empty: { textAlign: "center", color: "#9aa69e", fontSize: 13.5, padding: "26px 16px", border: "1px dashed #cfd8d2", borderRadius: 14, background: "#fff" },

  overlay: { position: "fixed", inset: 0, background: "rgba(13,28,21,.55)", backdropFilter: "blur(2px)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 50 },
  sheet: { width: "100%", maxWidth: 430, background: PAPER, borderRadius: "26px 26px 0 0", maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 -16px 50px rgba(13,28,21,.28)" },
  pdfSheet: { width: "100%", maxWidth: 430, height: "92vh", background: PAPER, borderRadius: "26px 26px 0 0", display: "flex", flexDirection: "column" },
  pdfScroll: { flex: 1, width: "100%", overflowY: "auto", background: "#fff" },
  pdfBtns: { display: "flex", gap: 10, padding: 14, borderTop: `1px solid ${HAIR}` },
  pdfOpen: { flex: 1, textAlign: "center", textDecoration: "none", background: "#F1EFE7", color: GREEN_SOLID, borderRadius: 13, padding: "14px", fontSize: 14.5, fontWeight: 800 },
  pdfDownload: { flex: 1, textAlign: "center", textDecoration: "none", background: GREEN, color: "#fff", borderRadius: 13, padding: "14px", fontSize: 14.5, fontWeight: 800 },
  sheetHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", borderBottom: `1px solid ${HAIR}` },
  sheetTitle: { fontSize: 18, fontWeight: 800, color: INK, letterSpacing: -0.3 },
  x: { background: "#EDEAE1", border: "none", width: 32, height: 32, borderRadius: 999, fontSize: 14, cursor: "pointer", color: "#6B7A70" },
  sheetBody: { padding: 20, overflowY: "auto", flex: 1, minHeight: 0 },
  sheetFooter: { padding: "14px 16px", borderTop: `1px solid ${HAIR}`, background: PAPER, flexShrink: 0 },

  fieldLabel: { fontSize: 12, fontWeight: 700, color: "#6B7A70", marginBottom: 7, letterSpacing: 0.2 },
  input: { width: "100%", boxSizing: "border-box", height: 48, padding: "12px 14px", borderRadius: 13, border: `1px solid ${HAIR}`, background: "#FCFBF8", fontSize: 15, fontFamily: "inherit", color: INK },
  dateInput: { width: "100%", boxSizing: "border-box", height: 48, padding: "12px 14px", borderRadius: 13, border: `1px solid ${HAIR}`, background: "#FCFBF8", fontSize: 15, fontFamily: "inherit", color: INK, textAlign: "left", lineHeight: "22px", WebkitAppearance: "none", MozAppearance: "none", appearance: "none" },

  splitBox: { background: "#FCFBF8", border: `1px solid ${HAIR}`, borderRadius: 14, padding: "14px 16px" },
  pctRow: { display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #f0f2f0" },
  pctInput: { marginLeft: "auto", width: 70, height: 40, textAlign: "center", padding: "6px 8px", borderRadius: 10, border: "1px solid #d8e0da", fontSize: 16, fontWeight: 800, fontFamily: "inherit", color: "#16241c" },
  pctSign: { fontSize: 14, color: "#8a978f", fontWeight: 700 },
  splitFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, fontSize: 13 },
  evenBtn: { background: "#F6EFDD", border: "1px solid #E7D9B0", color: "#7A5E1E", borderRadius: 999, padding: "8px 15px", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  slideRow: { marginBottom: 12 },
  slideHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 4 },
  slideName: { fontSize: 14, fontWeight: 700, color: "#2a382f" },
  slidePct: { marginLeft: "auto", fontSize: 15, fontWeight: 800 },
  range: { width: "100%", cursor: "pointer" },
  splitTotal: { fontSize: 11.5, color: "#8a978f", textAlign: "center", marginTop: 2 },

  payChip: { display: "flex", alignItems: "center", gap: 9, border: "2px solid", borderRadius: 11, padding: "11px 13px" },
  payNote: { marginLeft: "auto", fontSize: 11.5, color: "#8a978f", fontWeight: 600 },

  catPickRow: { display: "flex", gap: 8, alignItems: "center" },
  warnNote: { background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa", borderRadius: 10, padding: "8px 11px", fontSize: 12.5, fontWeight: 600, marginBottom: 12 },
  addCatBtn: { flexShrink: 0, background: "#e7f6ec", color: "#0f5132", border: "1px solid #cde8d6", borderRadius: 11, padding: "11px 12px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  uploadBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", boxSizing: "border-box", background: "#fff", border: "1px dashed #b9c7be", borderRadius: 12, padding: "16px", fontSize: 14.5, fontWeight: 700, color: "#0f5132", cursor: "pointer" },
  receiptBox: { display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #d8e0da", borderRadius: 12, padding: "10px 12px" },
  receiptImg: { width: 48, height: 48, objectFit: "cover", borderRadius: 8, flexShrink: 0 },
  receiptFileIcon: { fontSize: 28, width: 48, textAlign: "center", flexShrink: 0 },
  receiptName: { flex: 1, fontSize: 13, color: "#2a382f", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  receiptDel: { background: "none", border: "none", color: "#dc2626", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
  addCatPanel: { marginTop: 8, background: "#fff", border: "1px dashed #cfd8d2", borderRadius: 12, padding: 12 },
  addCatInputs: { display: "flex", gap: 8 },

  pRow: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #eef1ef" },
  pName: { fontSize: 15, fontWeight: 700, color: "#16241c" },
  pLock: { marginLeft: "auto", fontSize: 11.5, color: "#8a978f" },
  pRemove: { marginLeft: "auto", background: "none", border: "none", color: "#dc2626", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  pEdit: { background: "none", border: "none", color: "#0891b2", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0 },
  swatches: { display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0 14px" },
  swatch: { width: 30, height: 30, borderRadius: 999, border: "none", cursor: "pointer" },

  primary: { width: "100%", background: GREEN, color: "#fff", border: "1px solid rgba(228,206,146,.22)", borderRadius: 14, padding: "15px", fontSize: 16, fontWeight: 800, cursor: "pointer", marginTop: 6, fontFamily: "inherit", letterSpacing: 0.2, boxShadow: SHADOW },
  danger: { width: "100%", background: "none", color: "#dc2626", border: "none", padding: "13px", fontSize: 14.5, fontWeight: 700, cursor: "pointer", marginTop: 4, fontFamily: "inherit" },
  dangerArmed: { background: "#fde8e8", borderRadius: 12, fontWeight: 800 },
};

const mapStateToProps = (state) => ({
  partners: partnersSelector(state),
  houses: housesSelector(state),
  addableUsers: addableUsersSelector(state),
  currentPersonId: currentPersonIdSelector(state),
  role: roleSelector(state),
  canSeeAll: canSeeAllSelector(state),
});

const actionCreators = {
  listRealEstate,
  addPartnerUser: addPartnerUserAction,
  deletePartner: deletePartnerAction,
  saveHouse: saveHouseAction,
  deleteHouse: deleteHouseAction,
  saveExpense: saveExpenseAction,
  deleteExpense: deleteExpenseAction,
};

export default withShipment({ mapStateToProps, actionCreators }, RealEstate);
